import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Image as ImageIcon,
  Video,
  Calendar,
  Clock,
  Hash,
  Send,
  X,
  Check,
  AlertCircle,
  Upload,
  Wand2,
  Loader2,
  Trash2,
  Play,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  Film,
  ImagePlus,
  Palette,
  Building2,
  Settings as SettingsIcon,
  Plus,
  ArrowUp,
  ArrowDown,
  Layers,
  AtSign,
  Maximize2
} from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import Button from '../ui/Button';
import ImageStudioModal from '../image-studio/ImageStudioModal';
import ImageEditorModal from '../image-studio/ImageEditorModal';
import VideoFramePicker from '../shared/VideoFramePicker';
import AIImageReferenceInput, { AIImageReferenceHandle } from '../shared/AIImageReferenceInput';
import GalleryPicker from '../video-studio/GalleryPicker';
import socialService from '../../services/socialService';
import onboardingService, { BusinessProfile } from '../../services/onboardingService';
import { listeningService } from '../../services/listeningService';
import { useProject } from '../../contexts/ProjectContext';
import toast from 'react-hot-toast';
import { isHandledByInterceptor } from '../../utils/apiError';
import api from '../../services/api';
import AiCostBadge from '../ai/AiCostBadge';

// TikTok creator info — fetched from /api/oauth/tiktok/creator-info before showing
// the composer. Required by TikTok Content Sharing Developer Guidelines.
interface TiktokCreatorInfo {
  creatorAvatarUrl: string | null;
  creatorUsername: string | null;
  creatorNickname: string | null;
  privacyLevelOptions: string[];
  commentDisabled: boolean;
  duetDisabled: boolean;
  stitchDisabled: boolean;
  maxVideoPostDurationSec: number;
}

// Platform icons
const PlatformIcons: Record<string, React.FC<{ className?: string }>> = {
  instagram: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  facebook: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  ),
  twitter: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  ),
  linkedin: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
    </svg>
  ),
  youtube: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
  ),
  youtube_shorts: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M10 14.65v-5.3L15 12l-5 2.65zm7.77-4.33c-.77-.32-1.2-.5-1.2-.5L18 9.06c1.84-.96 2.53-3.23 1.56-5.06s-3.24-2.53-5.07-1.56L6 6.94c-1.29.68-2.07 2.04-2 3.49.07 1.42.93 2.67 2.22 3.25.03.01 1.2.5 1.2.5L6 14.93c-1.83.97-2.53 3.24-1.56 5.07.97 1.83 3.24 2.53 5.07 1.56l8.5-4.5c1.29-.68 2.06-2.04 1.99-3.49-.07-1.42-.94-2.68-2.23-3.25z"/>
    </svg>
  ),
  tiktok: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
  ),
  threads: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.332-3.023.88-.73 2.106-1.146 3.451-1.175 1.016-.022 1.96.139 2.818.468-.073-.938-.306-1.683-.7-2.216-.453-.614-1.143-.936-2.053-.958l-.023-.003c-.689 0-1.58.142-2.433.907l-1.416-1.486c1.249-1.12 2.664-1.538 4.001-1.538h.036c1.478.034 2.689.548 3.6 1.528.857.922 1.366 2.2 1.514 3.8.475.19.917.422 1.32.695 1.198.813 2.074 1.922 2.533 3.205.752 2.106.593 4.932-1.635 7.11C18.581 23.087 15.994 23.973 12.186 24z"/>
    </svg>
  ),
  instagram_reels: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
      <path d="M9.5 7.5l6 4.5-6 4.5z"/>
    </svg>
  ),
  // Stories reaproveitam o ícone da rede-mãe (IG/FB) — visualmente reconhecível.
  instagram_stories: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  ),
  facebook_stories: ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
  )
};

interface Platform {
  id: string;
  name: string;
  maxLength: number;
  color: string;
  connected: boolean;
}

interface ConnectedAccount {
  id: string;
  platform: string;
  username: string;
  profile_image?: string | null;
  connected: boolean;
}

interface AdvancedComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PostData) => Promise<void> | void;
  editingPost?: PostData | null;
  connectedAccounts?: ConnectedAccount[];
  isSubmitting?: boolean;
}

interface PostData {
  content: string;
  platforms: string[];
  scheduled_at?: string;
  media_urls: string[];
  hashtags: string[];
  status: 'draft' | 'scheduled' | 'publish_now';
  content_type?: 'post' | 'story' | 'reel' | 'carousel' | 'video';
  first_comment?: string;
  // Platform-specific fields
  metadata?: {
    youtube?: {
      title?: string;
      tags?: string;
      category?: string;
      visibility?: 'public' | 'unlisted' | 'private';
    };
    instagram?: {
      alt_text?: string;
      first_comment?: string;
      // Texto de cada slide do carrossel (índice 0 = capa). Guardado para o
      // round-trip de edição — a legenda publicada continua sendo `content`.
      carousel_slides?: string[];
    };
    // Carrossel só no Facebook (sem Instagram no post): os textos dos slides ficam aqui
    facebook?: {
      carousel_slides?: string[];
    };
    linkedin?: {
      article_mode?: boolean;
    };
    twitter?: {
      thread_mode?: boolean;
    };
    tiktok?: {
      privacy_level?: 'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY';
      comment_enabled?: boolean;
      duet_enabled?: boolean;
      stitch_enabled?: boolean;
      // "Disclose post content" master toggle (required by TikTok policy)
      brand_organic_toggle?: boolean; // "Your Brand" — promoting yourself
      brand_content_toggle?: boolean; // "Branded Content" — promoting a 3rd party
      video_cover_timestamp_ms?: number;
    };
    logo?: {
      url?: string;
      position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';
      size?: number; // percentage of container width (5-50)
      enabled?: boolean;
    };
  };
}

interface MediaItem {
  id: string;
  url: string;
  thumbnailUrl?: string;
  type: 'image' | 'video';
  // 'empty' = slide de carrossel criado a partir do texto, ainda sem imagem
  status: 'uploading' | 'ready' | 'error' | 'generating' | 'empty';
  progress?: number;
  duration?: number;
  fileSize?: number; // bytes — used for per-platform size limits (e.g. Instagram 1GB)
  slideText?: string; // texto do slide (carrossel) — vem da IA ou digitado pelo usuário
}

// Redes que publicam várias imagens num post só: Instagram (carrossel) e Facebook
// (post com várias fotos). Antes era só Instagram (Dentro da História, 03/11/2026).
const PLATAFORMAS_COM_CARROSSEL = ['instagram', 'facebook'];

// ===== Carrossel: detecção de slides na legenda =====
// A IA costuma estruturar a legenda como "Slide 1: ...", "**SLIDE 2 —** ..." ou
// "1/5: ...". Estes helpers reconhecem esses marcadores e fatiam o texto em
// slides para o usuário atribuir uma imagem a cada um.
const SLIDE_MARKER_RE = /(?:^|\n)[ \t]*[*_#>•-]{0,3}[ \t]*(?:slide|l[âa]mina|card|p[áa]gina)[ \t]*0?(\d{1,2})(?:[ \t]*\/[ \t]*\d{1,2})?[ \t]*(?:[:\-–—.)\]]+|\*\*|__)?[*_ \t]*/gi;
const SLIDE_FRACTION_RE = /(?:^|\n)[ \t]*0?(\d{1,2})[ \t]*\/[ \t]*\d{1,2}[ \t]*[:\-–—.)][ \t]*/g;

const sliceTextByMarkers = (text: string, matches: RegExpMatchArray[]): string[] | null => {
  const parts: string[] = [];
  for (let i = 0; i < matches.length; i++) {
    const start = (matches[i].index ?? 0) + matches[i][0].length;
    const end = i + 1 < matches.length ? (matches[i + 1].index ?? text.length) : text.length;
    const chunk = text.slice(start, end).replace(/\*{1,2}|_{1,2}/g, '').trim();
    if (chunk) parts.push(chunk);
  }
  return parts.length >= 2 ? parts.slice(0, 10) : null;
};

const parseSlidesFromText = (text: string): string[] | null => {
  if (!text || !text.trim()) return null;
  const byKeyword = Array.from(text.matchAll(SLIDE_MARKER_RE));
  if (byKeyword.length >= 2) return sliceTextByMarkers(text, byKeyword);
  const byFraction = Array.from(text.matchAll(SLIDE_FRACTION_RE));
  if (byFraction.length >= 2) return sliceTextByMarkers(text, byFraction);
  return null;
};

// Video format types
type VideoFormat = 'vertical' | 'horizontal' | 'any';

const platformConfig: Record<string, {
  name: string;
  maxLength: number;
  color: string;
  supportsImage: boolean;
  supportsVideo: boolean;
  requiresVideo: boolean;
  requiresThumbnail: boolean;
  videoFormat: VideoFormat;
  conflictsWith: string[];
}> = {
  instagram: { name: 'Instagram', maxLength: 2200, color: 'from-purple-500 to-pink-500', supportsImage: true, supportsVideo: true, requiresVideo: false, requiresThumbnail: false, videoFormat: 'any', conflictsWith: [] },
  instagram_reels: { name: 'Instagram Reels', maxLength: 2200, color: 'from-purple-600 to-pink-600', supportsImage: false, supportsVideo: true, requiresVideo: true, requiresThumbnail: false, videoFormat: 'vertical', conflictsWith: ['youtube'] },
  facebook: { name: 'Facebook', maxLength: 63206, color: 'from-blue-600 to-blue-500', supportsImage: true, supportsVideo: true, requiresVideo: false, requiresThumbnail: false, videoFormat: 'any', conflictsWith: [] },
  twitter: { name: 'X (Twitter)', maxLength: 280, color: 'from-gray-900 to-gray-700', supportsImage: true, supportsVideo: false, requiresVideo: false, requiresThumbnail: false, videoFormat: 'any', conflictsWith: [] },
  linkedin: { name: 'LinkedIn', maxLength: 3000, color: 'from-blue-700 to-blue-600', supportsImage: true, supportsVideo: true, requiresVideo: false, requiresThumbnail: false, videoFormat: 'any', conflictsWith: [] },
  youtube: { name: 'YouTube', maxLength: 5000, color: 'from-red-600 to-red-500', supportsImage: false, supportsVideo: true, requiresVideo: true, requiresThumbnail: true, videoFormat: 'horizontal', conflictsWith: ['youtube_shorts', 'tiktok', 'instagram_reels'] },
  youtube_shorts: { name: 'YouTube Shorts', maxLength: 100, color: 'from-red-500 to-orange-500', supportsImage: false, supportsVideo: true, requiresVideo: true, requiresThumbnail: false, videoFormat: 'vertical', conflictsWith: ['youtube'] },
  tiktok: { name: 'TikTok', maxLength: 2200, color: 'from-gray-900 to-pink-500', supportsImage: false, supportsVideo: true, requiresVideo: true, requiresThumbnail: false, videoFormat: 'vertical', conflictsWith: ['youtube'] },
  threads: { name: 'Threads', maxLength: 500, color: 'from-gray-900 to-gray-600', supportsImage: true, supportsVideo: false, requiresVideo: false, requiresThumbnail: false, videoFormat: 'any', conflictsWith: [] },
  // Stories: mídia única (1 imagem OU 1 vídeo ≤60s), vertical 9:16. Sem carrossel.
  // A legenda é ignorada pela API (mantemos maxLength só para o contador não quebrar).
  instagram_stories: { name: 'Instagram Stories', maxLength: 2200, color: 'from-purple-500 to-orange-400', supportsImage: true, supportsVideo: true, requiresVideo: false, requiresThumbnail: false, videoFormat: 'vertical', conflictsWith: [] },
  facebook_stories: { name: 'Facebook Stories', maxLength: 2200, color: 'from-blue-600 to-cyan-400', supportsImage: true, supportsVideo: true, requiresVideo: false, requiresThumbnail: false, videoFormat: 'vertical', conflictsWith: [] }
};

// Platform groups for better UX
const platformGroups = {
  vertical: ['tiktok', 'youtube_shorts', 'instagram_reels'], // Require vertical video (9:16)
  horizontal: ['youtube'], // Require horizontal video (16:9)
  flexible: ['instagram', 'facebook', 'twitter', 'linkedin', 'threads'], // Accept any format
  stories: ['instagram_stories', 'facebook_stories'] // Stories (9:16, mídia única, somem em 24h)
};

const defaultPlatforms: Platform[] = Object.entries(platformConfig).map(([id, config]) => ({
  id,
  name: config.name,
  maxLength: config.maxLength,
  color: config.color,
  connected: false
}));

// Helper: strip appended hashtags from content (they get concatenated on save)
function stripHashtagsFromContent(content: string, hashtags: string[]): string {
  if (!content || !hashtags || hashtags.length === 0) return content || '';
  // Try "\n\n#hash1 #hash2"
  const suffixNewline = '\n\n' + hashtags.join(' ');
  if (content.endsWith(suffixNewline)) {
    return content.slice(0, -suffixNewline.length).trim();
  }
  // Try " #hash1 #hash2"
  const suffixSpace = ' ' + hashtags.join(' ');
  if (content.endsWith(suffixSpace)) {
    return content.slice(0, -suffixSpace.length).trim();
  }
  // Fallback: try removing hashtags one by one from the end
  let cleaned = content;
  for (let i = hashtags.length - 1; i >= 0; i--) {
    const trimmed = cleaned.trimEnd();
    if (trimmed.endsWith(hashtags[i])) {
      cleaned = trimmed.slice(0, -hashtags[i].length);
    }
  }
  return cleaned.trim();
}

const AdvancedComposer: React.FC<AdvancedComposerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editingPost,
  connectedAccounts = [],
  isSubmitting = false
}) => {
  const { t, i18n } = useTranslation();
  const { currentProject } = useProject();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  // Monotonic tokens to discard stale upload callbacks (rapid re-select / remove during upload).
  // Separate per media slot so uploading one doesn't invalidate another in flight.
  const uploadSeqRef = useRef(0);
  const thumbSeqRef = useRef(0);
  const logoSeqRef = useRef(0);

  // Track the in-flight video multipart upload so we can clean up its parts if
  // the user replaces/removes the video, closes the composer, or leaves the page.
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const multipartAbortRef = useRef<{ key: string; uploadId: string } | null>(null);

  // Cancel + clean up an in-flight video upload (its S3 parts are aborted so they
  // don't linger/cost). Safe to call when nothing is uploading.
  const cancelVideoUpload = () => {
    if (uploadAbortControllerRef.current) {
      try { uploadAbortControllerRef.current.abort(); } catch { /* noop */ }
      uploadAbortControllerRef.current = null;
    }
    const mp = multipartAbortRef.current;
    multipartAbortRef.current = null;
    if (mp) socialService.abortMultipart(mp.key, mp.uploadId);
  };

  // Page-unload safety net: if a multipart upload is in flight when the tab is
  // closed, fire a keepalive abort so the parts don't orphan. (The S3 lifecycle
  // rule sweeps anything this misses, but this cleans up immediately.)
  useEffect(() => {
    const onPageHide = () => {
      const mp = multipartAbortRef.current;
      if (!mp) return;
      try {
        const token = localStorage.getItem('token');
        fetch(`${process.env.REACT_APP_API_URL}/media/multipart/abort`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ key: mp.key, uploadId: mp.uploadId })
        });
      } catch { /* best-effort */ }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Helper function to detect video dimensions and auto-select YouTube/Shorts
  const detectVideoAndSelectPlatform = (videoFile: File): Promise<{ width: number; height: number; duration: number }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const cleanup = () => {
        video.onloadedmetadata = null;
        video.onerror = null;
        URL.revokeObjectURL(video.src);
      };
      // Some files never fire onloadedmetadata (corrupt, unsupported codec) —
      // without this timeout the whole upload flow would hang silently.
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Timed out reading video metadata'));
      }, 15000);
      video.onloadedmetadata = () => {
        clearTimeout(timer);
        const width = video.videoWidth;
        const height = video.videoHeight;
        // Streamed/fragmented files sometimes report Infinity duration.
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        cleanup();
        resolve({ width, height, duration });
      };
      video.onerror = () => {
        clearTimeout(timer);
        cleanup();
        reject(new Error('Could not read video metadata'));
      };
      video.src = URL.createObjectURL(videoFile);
    });
  };

  // Video dimension info is stored but platforms are NOT auto-selected.
  // Users choose their target platforms manually.

  // Platforms that share authentication (e.g., youtube_shorts uses youtube's connection)
  const linkedPlatforms: Record<string, string> = {
    youtube_shorts: 'youtube',
    instagram_reels: 'instagram',
    // Stories herdam a conexão da rede-mãe: IG Story exige IG conectado; FB Story
    // exige Facebook conectado (é o sinal prático de "IG vinculado ao Facebook").
    instagram_stories: 'instagram',
    facebook_stories: 'facebook'
  };

  // Build platforms list based on connected accounts
  const platforms: Platform[] = useMemo(() => {
    if (connectedAccounts.length === 0) {
      return defaultPlatforms;
    }
    return defaultPlatforms.map(p => {
      // Check direct connection
      const directConnection = connectedAccounts.some(a => a.platform === p.id && a.connected);
      // Check linked platform connection (e.g., youtube_shorts -> youtube)
      const linkedTo = linkedPlatforms[p.id];
      const linkedConnection = linkedTo ? connectedAccounts.some(a => a.platform === linkedTo && a.connected) : false;

      return {
        ...p,
        connected: directConnection || linkedConnection
      };
    });
  }, [connectedAccounts]);

  // State
  const [content, setContent] = useState(() => stripHashtagsFromContent(editingPost?.content || '', editingPost?.hashtags || []));

  // ----- Menções por @ na legenda -----
  // mentionQuery null = autocomplete inativo; string (pode ser '') = digitando após '@'.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  // Handles das marcas monitoradas no Listening (sugestões além das contas conectadas).
  const [brandHandles, setBrandHandles] = useState<string[]>([]);

  // Busca handles das marcas do Listening ao abrir (best-effort — se falhar, segue
  // só com as contas conectadas como fonte de sugestão de menção).
  useEffect(() => {
    if (!isOpen || !currentProject?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await listeningService.getBrands(currentProject.id);
        if (cancelled) return;
        const list: any[] = Array.isArray(res) ? res : (res?.data || res?.brands || []);
        const handles: string[] = [];
        list.forEach((b: any) => {
          if (b?.handles && typeof b.handles === 'object') {
            Object.values(b.handles).forEach((h: any) => {
              if (h && typeof h === 'string') handles.push(h.replace(/^@/, '').trim());
            });
          }
        });
        setBrandHandles([...new Set(handles.filter(Boolean))]);
      } catch {
        /* sem Listening configurado / sem permissão — ignora */
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, currentProject?.id]);

  // Pool de sugestões de menção: contas conectadas + marcas do Listening.
  const mentionPool = useMemo(() => {
    const fromAccounts = (connectedAccounts || [])
      .map(a => (a.username || '').replace(/^@/, '').trim())
      .filter(Boolean);
    return [...new Set([...fromAccounts, ...brandHandles])];
  }, [connectedAccounts, brandHandles]);

  const mentionMatches = useMemo(() => {
    if (mentionQuery == null) return [];
    const q = mentionQuery.toLowerCase();
    return mentionPool
      .filter(h => h.toLowerCase().startsWith(q) && h.toLowerCase() !== q)
      .slice(0, 6);
  }, [mentionQuery, mentionPool]);

  // Detecta se o cursor está logo após um token "@parcial" para abrir o autocomplete.
  // [\w.] aceita handles com ponto (ex.: andrew.cabecaonca) — \w sozinho cortava no ponto.
  const updateMentionState = (el: HTMLTextAreaElement) => {
    const caret = el.selectionStart ?? 0;
    const before = el.value.slice(0, caret);
    const m = before.match(/(?:^|\s)@([\w.]{0,30})$/);
    setMentionQuery(m ? m[1] : null);
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    updateMentionState(e.target);
  };

  // Substitui o token "@..." sob o cursor por "@handle ". Considera também o
  // fragmento DEPOIS do cursor (cursor no meio da palavra) e vira no-op se não há
  // token no cursor (ex.: dropdown ficou aberto após geração por IA).
  const insertMention = (handle: string) => {
    const el = contentRef.current;
    const caret = el?.selectionStart ?? content.length;
    const before = content.slice(0, caret);
    const after = content.slice(caret);
    const m = before.match(/@([\w.]{0,30})$/);
    if (!m) { setMentionQuery(null); return; }
    const tokenStart = before.length - m[0].length;      // índice do '@'
    const trailing = (after.match(/^[\w.]*/) || [''])[0]; // resto da palavra após o cursor
    const newBefore = content.slice(0, tokenStart) + `@${handle} `;
    setContent(newBefore + after.slice(trailing.length));
    setMentionQuery(null);
    const newCaret = newBefore.length;
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(newCaret, newCaret); } catch { /* noop */ }
    });
  };

  // Botão "Mencionar": insere '@' no cursor e abre o autocomplete (lista todas as
  // sugestões). Torna a menção uma opção visível, além de digitar '@' direto.
  const startMention = () => {
    const el = contentRef.current;
    const caret = el?.selectionStart ?? content.length;
    const before = content.slice(0, caret);
    const after = content.slice(caret);
    const needsSpace = before.length > 0 && !/\s$/.test(before);
    const newBefore = before + (needsSpace ? ' @' : '@');
    setContent(newBefore + after);
    setMentionQuery(''); // q vazio => mostra todas as sugestões do pool
    const newCaret = newBefore.length;
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(newCaret, newCaret); } catch { /* noop */ }
    });
  };

  // Seção "Menções": guarda os @handles num estado próprio (NÃO no texto), para
  // a IA não sobrescrever. São anexados à legenda apenas no submit.
  const [mentionInput, setMentionInput] = useState('');
  const addMentionHandle = (raw: string) => {
    const handle = String(raw || '').trim().replace(/^@+/, '');
    if (!handle) return;
    setSelectedMentions(prev => prev.includes(handle) ? prev : [...prev, handle]);
    setMentionInput('');
  };
  const removeMentionHandle = (handle: string) => {
    setSelectedMentions(prev => prev.filter(h => h !== handle));
  };
  // Monta a legenda final = texto + menções + hashtags (cada bloco separado por \n\n).
  const buildFinalContent = (body: string) => {
    const mentionStr = selectedMentions.map(h => '@' + h).join(' ');
    const tagStr = selectedHashtags.join(' ');
    return [body, mentionStr, tagStr].map(s => (s || '').trim()).filter(Boolean).join('\n\n');
  };
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(editingPost?.platforms || []);
  // Populated from editingPost by the reset effect (via formatDateForInput → the
  // datetime-local format). Don't seed the raw UTC ISO here — it's not a valid
  // datetime-local value and the effect would have to overwrite it anyway.
  const [scheduledAt, setScheduledAt] = useState('');
  const [videoItem, setVideoItem] = useState<MediaItem | null>(null);
  // Keep a local File reference so we can extract frames without CORS issues against S3
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [showFramePicker, setShowFramePicker] = useState(false);
  const [thumbnailItem, setThumbnailItem] = useState<MediaItem | null>(null);
  const [logoItem, setLogoItem] = useState<MediaItem | null>(null);
  // Extra carousel slides (Instagram e Facebook). Thumbnail is always slide 1; these are 2..N.
  const [extraCarouselItems, setExtraCarouselItems] = useState<MediaItem[]>([]);
  const [isGeneratingCarouselAi, setIsGeneratingCarouselAi] = useState(false);
  const [carouselAiCount, setCarouselAiCount] = useState<number>(3);
  const carouselFileInputRef = useRef<HTMLInputElement | null>(null);
  // Texto do slide 1 (capa) do carrossel — extras guardam o próprio texto em slideText.
  const [coverSlideText, setCoverSlideText] = useState('');
  // Upload de imagem para UM slide específico: 'cover' ou o id do slide extra.
  const [slideUploadTarget, setSlideUploadTarget] = useState<string | null>(null);
  const slideReplaceInputRef = useRef<HTMLInputElement | null>(null);
  // Índice do slide exibido no preview do carrossel.
  const [previewSlideIdx, setPreviewSlideIdx] = useState(0);
  const [addLogoToThumbnail, setAddLogoToThumbnail] = useState(false);
  const [logoPosition, setLogoPosition] = useState<'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center'>('bottom-right');
  const [logoSize, setLogoSize] = useState<number>(20); // percentage of container width
  const [showImageStudio, setShowImageStudio] = useState(false);
  // Library picker — 'video' picks for the video slot, 'image' for the thumbnail slot
  const [showLibrary, setShowLibrary] = useState<'video' | 'image' | null>(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [selectedHashtags, setSelectedHashtags] = useState<string[]>(editingPost?.hashtags || []);
  // Menções ficam SEPARADAS do texto (estado próprio, como hashtags) para que a
  // geração de conteúdo por IA não as apague. São anexadas na legenda só ao publicar.
  const [selectedMentions, setSelectedMentions] = useState<string[]>([]);
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [showHashtagSuggestions, setShowHashtagSuggestions] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState(editingPost?.platforms?.[0] || selectedPlatforms[0] || '');
  const [hashtagInput, setHashtagInput] = useState('');

  // AI Generation state
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiImageStyle, setAiImageStyle] = useState<'realistic' | 'artistic' | 'minimal' | 'vibrant' | 'professional'>('professional');
  const [aiImageRatio, setAiImageRatio] = useState<'1:1' | '4:5' | '16:9' | '9:16'>('1:1');
  const [aiVideoStyle, setAiVideoStyle] = useState<'cinematic' | 'dynamic' | 'minimal' | 'professional'>('professional');
  const [aiVideoDuration, setAiVideoDuration] = useState<15 | 30 | 60>(15);
  const [ignorePattern, setIgnorePattern] = useState(false);
  const [variationDirection, setVariationDirection] = useState('');

  // Reference image for AI image generation — handled by shared component
  const aiRefHandle = useRef<AIImageReferenceHandle | null>(null);
  const [isUploadingAiRef, setIsUploadingAiRef] = useState(false);

  // Brand / business profile for banner + AI context
  const [brandProfile, setBrandProfile] = useState<BusinessProfile | null>(null);
  const [brandProfileLoaded, setBrandProfileLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    onboardingService.getProfile()
      .then(profile => {
        if (!active) return;
        setBrandProfile(profile);
      })
      .catch(() => {
        // silently ignore — banner just won't show
      })
      .finally(() => {
        if (active) setBrandProfileLoaded(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // Platform-specific fields state
  // YouTube
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [youtubeTags, setYoutubeTags] = useState('');
  const [youtubeCategory, setYoutubeCategory] = useState('Entertainment');
  const [youtubeVisibility, setYoutubeVisibility] = useState<'public' | 'unlisted' | 'private'>('public');
  // Instagram
  const [instagramLocation, setInstagramLocation] = useState('');
  const [instagramAltText, setInstagramAltText] = useState('');
  const [instagramFirstComment, setInstagramFirstComment] = useState('');
  // Marcar contas NA FOTO do feed do Instagram (user_tags) — separado da legenda.
  const [igUserTags, setIgUserTags] = useState<string[]>([]);
  const [igUserTagInput, setIgUserTagInput] = useState('');
  const addIgUserTag = (raw: string) => {
    const h = String(raw || '').trim().replace(/^@+/, '');
    if (!h) return;
    setIgUserTags(prev => prev.includes(h) ? prev : [...prev, h]);
    setIgUserTagInput('');
  };
  // LinkedIn
  const [linkedinArticleMode, setLinkedinArticleMode] = useState(false);
  // Twitter
  const [twitterThreadMode, setTwitterThreadMode] = useState(false);
  // TikTok — every field below MUST be a deliberate user choice. The composer
  // pulls allowed privacy levels and account-level interaction limits from
  // creator_info (see fetch below). Hardcoded defaults trigger audit rejection.
  const [tiktokCreatorInfo, setTiktokCreatorInfo] = useState<TiktokCreatorInfo | null>(null);
  const [tiktokCreatorInfoLoading, setTiktokCreatorInfoLoading] = useState(false);
  const [tiktokCreatorInfoError, setTiktokCreatorInfoError] = useState<string | null>(null);
  const [tiktokPrivacyLevel, setTiktokPrivacyLevel] = useState<'PUBLIC_TO_EVERYONE' | 'MUTUAL_FOLLOW_FRIENDS' | 'FOLLOWER_OF_CREATOR' | 'SELF_ONLY' | ''>('');
  // TikTok Point 2: interaction toggles must NOT be pre-checked by default.
  const [tiktokCommentEnabled, setTiktokCommentEnabled] = useState(false);
  const [tiktokDuetEnabled, setTiktokDuetEnabled] = useState(false);
  const [tiktokStitchEnabled, setTiktokStitchEnabled] = useState(false);
  // Music Usage Confirmation — required acknowledgment before publishing (Point 2)
  const [tiktokMusicConfirmed, setTiktokMusicConfirmed] = useState(false);
  // Disclose post content master toggle. When OFF, both branded sub-toggles
  // must also be OFF. TikTok requires this consent UX before publish.
  const [tiktokDiscloseEnabled, setTiktokDiscloseEnabled] = useState(false);
  const [tiktokBrandOrganic, setTiktokBrandOrganic] = useState(false); // "Your Brand"
  const [tiktokBrandContent, setTiktokBrandContent] = useState(false); // "Branded Content"
  // Instagram Trial Reel: publish to non-followers first, decide later whether to share to followers
  const [trialReelEnabled, setTrialReelEnabled] = useState(false);
  const [trialGraduationStrategy, setTrialGraduationStrategy] = useState<'MANUAL' | 'SS_PERFORMANCE'>('MANUAL');

  // Video preview modal
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  // Imagem do post em tamanho inteiro (a miniatura corta a imagem em quadrado)
  const [showImagePreview, setShowImagePreview] = useState(false);
  useEffect(() => {
    if (!showImagePreview) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowImagePreview(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showImagePreview]);
  const [showMobilePreview, setShowMobilePreview] = useState(false);

  // Video/image dimensions for validation
  const [videoDimensions, setVideoDimensions] = useState<{ width: number; height: number } | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  // Helper to convert ISO date to datetime-local format (for input display).
  // The input shows the date in the browser's LOCAL timezone.
  const formatDateForInput = (isoDate: string | undefined): string => {
    if (!isoDate) return '';
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return '';
      // Format: YYYY-MM-DDTHH:mm (required by datetime-local input)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return '';
    }
  };

  // Convert the datetime-local value ("2026-04-21T09:00") from the user's LOCAL
  // timezone to an ISO 8601 UTC string before sending to the backend.
  // Without this, MySQL/Sequelize interprets the bare string as server-local
  // (usually UTC) and the post ends up scheduled 3h off for São Paulo users.
  const localDatetimeToUTC = (local: string | undefined): string | undefined => {
    if (!local) return undefined;
    // new Date("YYYY-MM-DDTHH:mm") with no TZ is parsed as browser-local — exactly what we want
    const d = new Date(local);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString();
  };

  // Reset form when modal closes or editingPost changes
  useEffect(() => {
    if (!isOpen) {
      // Always reset when modal closes — abort any in-flight video upload so its
      // S3 parts get cleaned up instead of orphaning. Bump the seq first so the
      // aborted upload's catch sees it as stale and stays silent (no error toast).
      uploadSeqRef.current++;
      cancelVideoUpload();
      setContent('');
      setSelectedPlatforms([]);
      setScheduledAt('');
      setVideoItem(null);
      setThumbnailItem(null);
      setLogoItem(null);
      setExtraCarouselItems([]);
      setCoverSlideText('');
      setSlideUploadTarget(null);
      setPreviewSlideIdx(0);
      setIsGeneratingCarouselAi(false);
      setCarouselAiCount(3);
      setAddLogoToThumbnail(false);
      setLogoPosition('bottom-right');
      setLogoSize(20);
      setSelectedHashtags([]);
      setSelectedMentions([]);
      setYoutubeTitle('');
      setYoutubeTags('');
      setYoutubeCategory('Entertainment');
      setYoutubeVisibility('public');
      setInstagramAltText('');
      setInstagramFirstComment('');
      setIgUserTags([]);
      setLinkedinArticleMode(false);
      setTwitterThreadMode(false);
      setTiktokDuetEnabled(false);
      setTiktokStitchEnabled(false);
      setTiktokPrivacyLevel('');
      setTiktokCommentEnabled(false);
      setTiktokDiscloseEnabled(false);
      setTiktokBrandOrganic(false);
      setTiktokBrandContent(false);
      setTiktokMusicConfirmed(false);
      setTiktokCreatorInfo(null);
      setTiktokCreatorInfoError(null);
      setTrialReelEnabled(false);
      setTrialGraduationStrategy('MANUAL');
      setShowAIPanel(false);
      setAiPrompt('');
      setHashtagInput('');
      return;
    }
    if (editingPost) {
      setContent(stripHashtagsFromContent(editingPost.content || '', editingPost.hashtags || []));
      setSelectedPlatforms(editingPost.platforms || []);
      // Only prefill the schedule date if it's still in the future. A stale past
      // date (old/failed post being re-saved) would leave the Schedule button
      // enabled but always failing validation — clear it so the user re-picks.
      setScheduledAt(
        editingPost.scheduled_at && new Date(editingPost.scheduled_at).getTime() > Date.now()
          ? formatDateForInput(editingPost.scheduled_at)
          : ''
      );
      setSelectedHashtags(editingPost.hashtags || []);
      setSelectedMentions([]);

      // Convert media URLs to video + thumbnail (+ carousel extras when applicable).
      // Os textos de cada slide (se salvos) voltam de metadata.instagram.carousel_slides
      // (ou metadata.facebook.carousel_slides, no carrossel só de Facebook):
      // índice 0 = capa, 1..N = extras. Textos além das imagens existentes viram
      // slides vazios ('empty') para o rascunho não perder o roteiro do carrossel.
      const slidesSalvos = (editingPost.metadata?.instagram as any)?.carousel_slides
        ?? (editingPost.metadata as any)?.facebook?.carousel_slides;
      const savedSlideTexts: string[] = Array.isArray(slidesSalvos)
        ? (slidesSalvos as any[]).map(s => String(s || ''))
        : [];
      setCoverSlideText(savedSlideTexts[0] || '');
      const extraSlideTexts = savedSlideTexts.slice(1);

      {
        const mediaUrls = editingPost.media_urls || [];
        const isVideoUrl = (url: string) =>
          url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') || url.includes('/video/');
        const videoUrl = mediaUrls.find(isVideoUrl);
        const imageUrls = mediaUrls.filter(u => !isVideoUrl(u));

        setVideoItem(videoUrl
          ? { id: 'existing-video', url: videoUrl, type: 'video', status: 'ready' }
          : null);

        setThumbnailItem(imageUrls.length > 0
          ? { id: 'existing-thumb', url: imageUrls[0], type: 'image', status: 'ready' }
          : null);

        const extras: MediaItem[] = imageUrls.slice(1).map((url, idx) => ({
          id: `existing-slide-${idx}`,
          url,
          type: 'image' as const,
          status: 'ready' as const,
          slideText: extraSlideTexts[idx] || undefined
        }));
        // Slides que tinham texto salvo mas ainda não tinham imagem
        for (let i = extras.length; i < extraSlideTexts.length; i++) {
          if (!extraSlideTexts[i]?.trim()) continue;
          extras.push({
            id: `existing-empty-slide-${i}`,
            url: '',
            type: 'image',
            status: 'empty',
            slideText: extraSlideTexts[i]
          });
        }
        setExtraCarouselItems(extras);
      }

      // Load platform-specific metadata
      const meta = editingPost.metadata;
      if (meta) {
        // YouTube
        if (meta.youtube) {
          setYoutubeTitle(meta.youtube.title || '');
          setYoutubeTags(meta.youtube.tags || '');
          setYoutubeCategory(meta.youtube.category || 'Entertainment');
          setYoutubeVisibility(meta.youtube.visibility || 'public');
        }
        // Instagram
        if (meta.instagram) {
          setInstagramAltText(meta.instagram.alt_text || '');
          setInstagramFirstComment(meta.instagram.first_comment || editingPost.first_comment || '');
          const trial = (meta.instagram as any)?.trial_reel;
          if (trial?.enabled) {
            setTrialReelEnabled(true);
            setTrialGraduationStrategy(trial.graduation_strategy === 'SS_PERFORMANCE' ? 'SS_PERFORMANCE' : 'MANUAL');
          }
        } else if (editingPost.first_comment) {
          setInstagramFirstComment(editingPost.first_comment);
        }
        // LinkedIn
        if (meta.linkedin) {
          setLinkedinArticleMode(meta.linkedin.article_mode || false);
        }
        // Twitter
        if (meta.twitter) {
          setTwitterThreadMode(meta.twitter.thread_mode || false);
        }
        // TikTok — defaults are intentionally false (Point 2: no interaction
        // pre-checked). When editing an existing post, persist whatever the
        // user originally chose; if the field was never set, keep it off.
        if (meta.tiktok) {
          setTiktokDuetEnabled(meta.tiktok.duet_enabled ?? false);
          setTiktokStitchEnabled(meta.tiktok.stitch_enabled ?? false);
          setTiktokCommentEnabled(meta.tiktok.comment_enabled ?? false);
          setTiktokPrivacyLevel((meta.tiktok.privacy_level as any) || '');
          const organic = !!meta.tiktok.brand_organic_toggle;
          const branded = !!meta.tiktok.brand_content_toggle;
          setTiktokBrandOrganic(organic);
          setTiktokBrandContent(branded);
          setTiktokDiscloseEnabled(organic || branded);
          // Music confirmation is a per-publish acknowledgment, not stored.
          // Force re-acknowledgment when re-opening an edit.
          setTiktokMusicConfirmed(false);
        }
        // Logo
        if (meta.logo && meta.logo.enabled && meta.logo.url) {
          setLogoItem({ id: 'existing-logo', url: meta.logo.url, type: 'image', status: 'ready' });
          setAddLogoToThumbnail(true);
          setLogoPosition(meta.logo.position || 'bottom-right');
          setLogoSize(meta.logo.size || 20);
        }
      }
    } else {
      // Reset all fields
      setContent('');
      setSelectedPlatforms([]);
      setScheduledAt('');
      setVideoItem(null);
      setThumbnailItem(null);
      setLogoItem(null);
      setExtraCarouselItems([]);
      setCoverSlideText('');
      setSlideUploadTarget(null);
      setPreviewSlideIdx(0);
      setAddLogoToThumbnail(false);
      setLogoPosition('bottom-right');
      setLogoSize(20);
      setSelectedHashtags([]);
      setSelectedMentions([]);
      // Reset platform-specific fields
      setYoutubeTitle('');
      setYoutubeTags('');
      setYoutubeCategory('Entertainment');
      setYoutubeVisibility('public');
      setInstagramAltText('');
      setInstagramFirstComment('');
      setIgUserTags([]);
      setLinkedinArticleMode(false);
      setTwitterThreadMode(false);
      setTiktokDuetEnabled(false);
      setTiktokStitchEnabled(false);
      setTiktokPrivacyLevel('');
      setTiktokCommentEnabled(false);
      setTiktokDiscloseEnabled(false);
      setTiktokBrandOrganic(false);
      setTiktokBrandContent(false);
      setTiktokMusicConfirmed(false);
      setTiktokCreatorInfo(null);
      setTiktokCreatorInfoError(null);
      setTrialReelEnabled(false);
      setTrialGraduationStrategy('MANUAL');
    }
  }, [editingPost, isOpen]);

  // Fetch TikTok creator info whenever the user selects TikTok in the composer.
  // The composer is required by the TikTok Content Sharing Guidelines to show
  // privacy options, account-level interaction limits, creator preview and
  // max video duration sourced from this endpoint — never hardcoded.
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedPlatforms.includes('tiktok')) {
      // Clear any stale state when user removes TikTok from the selection
      if (tiktokCreatorInfo || tiktokCreatorInfoError) {
        setTiktokCreatorInfo(null);
        setTiktokCreatorInfoError(null);
      }
      return;
    }
    if (tiktokCreatorInfo || tiktokCreatorInfoLoading) return;

    const tiktokAccount = connectedAccounts.find(a => a.platform === 'tiktok' && a.connected);
    if (!tiktokAccount) {
      setTiktokCreatorInfoError('No connected TikTok account.');
      return;
    }

    setTiktokCreatorInfoLoading(true);
    setTiktokCreatorInfoError(null);
    api.get('/oauth/tiktok/creator-info', { params: { accountId: tiktokAccount.id } })
      .then(res => {
        const info: TiktokCreatorInfo = res.data?.data;
        setTiktokCreatorInfo(info);
        // TikTok Content Sharing Guidelines (Point 2) explicitly forbid a default
        // privacy level — the user must consciously pick one. Do not pre-select.
        // If the account disables comments/duet/stitch, force the toggles off
        if (info?.commentDisabled) setTiktokCommentEnabled(false);
        if (info?.duetDisabled) setTiktokDuetEnabled(false);
        if (info?.stitchDisabled) setTiktokStitchEnabled(false);
      })
      .catch(err => {
        const msg = err?.response?.data?.message || err?.message || 'Failed to load TikTok creator info';
        setTiktokCreatorInfoError(msg);
      })
      .finally(() => setTiktokCreatorInfoLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedPlatforms, connectedAccounts]);

  // Enforce TikTok rule: Branded Content cannot be combined with SELF_ONLY privacy.
  // If the user toggles Branded Content on while privacy is SELF_ONLY, move to the
  // first non-SELF_ONLY option available.
  useEffect(() => {
    if (!tiktokBrandContent) return;
    if (tiktokPrivacyLevel !== 'SELF_ONLY') return;
    const next = tiktokCreatorInfo?.privacyLevelOptions?.find(p => p !== 'SELF_ONLY');
    if (next) setTiktokPrivacyLevel(next as any);
  }, [tiktokBrandContent, tiktokPrivacyLevel, tiktokCreatorInfo]);

  // Get the minimum max length from selected platforms
  const getMinMaxLength = () => {
    const selected = platforms.filter(p => selectedPlatforms.includes(p.id));
    if (selected.length === 0) return 2200;
    return Math.min(...selected.map(p => p.maxLength));
  };

  const minMaxLength = getMinMaxLength();
  // Calculate total content length including hashtags (they are appended on submit)
  // Conta o tamanho real da legenda final (texto + menções + hashtags), como será publicada.
  const totalContentLength = buildFinalContent(content).length;
  const isOverLimit = totalContentLength > minMaxLength;
  // A post is valid with a caption OR ready media (e.g. a no-caption Reel). Used
  // to enable the action buttons and explain why they're disabled.
  const hasReadyMedia = videoItem?.status === 'ready'
    || thumbnailItem?.status === 'ready'
    || extraCarouselItems.some(it => it.status === 'ready');
  // Any media still uploading/generating — the buttons must stay disabled until
  // it finishes (handleSubmit blocks it too, but the button should reflect it).
  const mediaUploading = videoItem?.status === 'uploading' || videoItem?.status === 'generating'
    || thumbnailItem?.status === 'uploading' || thumbnailItem?.status === 'generating'
    || extraCarouselItems.some(it => it.status === 'uploading' || it.status === 'generating');
  const canSubmit = (content.trim().length > 0 || hasReadyMedia) && selectedPlatforms.length > 0 && !isOverLimit && !mediaUploading;
  // Human-readable reason the action buttons are disabled, shown as a tooltip so
  // the user isn't left staring at a greyed-out button with no explanation.
  const submitBlockedReason = selectedPlatforms.length === 0
    ? t('social.selectPlatformFirst', 'Selecione pelo menos uma plataforma')
    : mediaUploading
      ? t('social.mediaStillUploadingShort', 'Aguarde o upload da mídia terminar')
      : (content.trim().length === 0 && !hasReadyMedia)
        ? t('social.captionOrMediaRequired', 'Escreva uma legenda ou anexe uma mídia')
        : isOverLimit
          ? t('social.contentOverLimitShort', 'Reduza o texto: acima do limite de caracteres')
          : null;

  // TikTok composer is gated by an explicit set of consent UX rules required by the
  // Content Sharing Developer Guidelines. Mirroring the rules here so the publish
  // buttons stay visually disabled (Point 3: "Publish button disables if toggle is
  // on but nothing is selected") until every requirement is met.
  //
  // Point 1 also requires that publishing HALTS when creator_info is unavailable
  // — so a loading/error state in creator_info also blocks submit.
  const tiktokSelected = selectedPlatforms.includes('tiktok');
  const tiktokCreatorInfoReady = !!tiktokCreatorInfo && !tiktokCreatorInfoError && !tiktokCreatorInfoLoading;
  const tiktokSubmitBlocked = tiktokSelected && (
    !tiktokCreatorInfoReady ||
    !tiktokPrivacyLevel ||
    !tiktokMusicConfirmed ||
    (tiktokDiscloseEnabled && !tiktokBrandOrganic && !tiktokBrandContent) ||
    (tiktokDiscloseEnabled && tiktokBrandContent && tiktokPrivacyLevel === 'SELF_ONLY') ||
    (
      // Server-known max duration check. videoItem.duration is in seconds when
      // available — only block when both values are present.
      typeof videoItem?.duration === 'number' &&
      typeof tiktokCreatorInfo?.maxVideoPostDurationSec === 'number' &&
      videoItem.duration > tiktokCreatorInfo.maxVideoPostDurationSec
    ) ||
    // Caption + hashtags would exceed TikTok's 2200 char limit (counts the
    // final string the worker actually sends, not just `content`).
    totalContentLength > 2200
  );

  // Check if a platform conflicts with currently selected platforms
  const getConflictingPlatform = (platformId: string): string | null => {
    const config = platformConfig[platformId];
    if (!config) return null;

    // Check direct conflicts
    for (const selected of selectedPlatforms) {
      if (config.conflictsWith.includes(selected)) {
        return selected;
      }
      // Check reverse conflict
      const selectedConfig = platformConfig[selected];
      if (selectedConfig?.conflictsWith.includes(platformId)) {
        return selected;
      }
    }

    // Orientation incompatibility is intrinsic to the platforms, not the file:
    // a vertical-only platform (Reels/Shorts/TikTok) can never share a post with a
    // horizontal-only one (YouTube). Derive it from videoFormat directly — and run
    // it even before a video is attached — so the combination can never reach the
    // upload step as a dead-end, regardless of the hand-maintained conflictsWith lists.
    const newFormat = config.videoFormat;
    if (newFormat === 'vertical' || newFormat === 'horizontal') {
      for (const selected of selectedPlatforms) {
        const selectedFormat = platformConfig[selected]?.videoFormat;
        if ((selectedFormat === 'vertical' || selectedFormat === 'horizontal') && selectedFormat !== newFormat) {
          return selected;
        }
      }
    }

    // Check video format conflicts when video is uploaded
    if (videoDimensions) {
      const isVertical = videoDimensions.height > videoDimensions.width;
      const newPlatformFormat = config.videoFormat;

      // If new platform requires specific format, check compatibility
      if (newPlatformFormat === 'vertical' && !isVertical) {
        return null; // Will be caught by video validation
      }
      if (newPlatformFormat === 'horizontal' && isVertical) {
        return null; // Will be caught by video validation
      }

      // Check if adding this platform creates format conflict with existing selections
      for (const selected of selectedPlatforms) {
        const selectedFormat = platformConfig[selected]?.videoFormat;
        if (selectedFormat === 'any' || newPlatformFormat === 'any') continue;
        if (selectedFormat !== newPlatformFormat) {
          return selected;
        }
      }
    }

    return null;
  };

  // Get disabled reason for a platform
  const getPlatformDisabledReason = (platformId: string): string | null => {
    const config = platformConfig[platformId];
    if (!config) return null;

    // Orientation dead-end gets a clearer message than the generic conflict.
    const fmt = config.videoFormat;
    if (fmt === 'vertical' || fmt === 'horizontal') {
      const clash = selectedPlatforms.find(p => {
        const f = platformConfig[p]?.videoFormat;
        return (f === 'vertical' || f === 'horizontal') && f !== fmt;
      });
      if (clash) {
        return t('social.orientationConflict', {
          platform: config.name,
          other: platformConfig[clash]?.name,
          defaultValue: `${config.name} e ${platformConfig[clash]?.name} exigem orientações de vídeo diferentes — publique-os separadamente.`
        });
      }
    }

    // Check conflicts
    const conflicting = getConflictingPlatform(platformId);
    if (conflicting) {
      const conflictName = platformConfig[conflicting]?.name;
      return t('social.conflictsWith', { platform: conflictName, defaultValue: `Conflicts with ${conflictName}` });
    }

    // Check video format if video is uploaded
    if (videoDimensions && config.requiresVideo) {
      const isVertical = videoDimensions.height > videoDimensions.width;
      if (config.videoFormat === 'vertical' && !isVertical) {
        return t('social.requiresVerticalVideo', 'Requires vertical video');
      }
      if (config.videoFormat === 'horizontal' && isVertical) {
        return t('social.requiresHorizontalVideo', 'Requires horizontal video');
      }
    }

    return null;
  };

  const togglePlatform = (platformId: string) => {
    // If deselecting, always allow
    if (selectedPlatforms.includes(platformId)) {
      setSelectedPlatforms(prev => prev.filter(p => p !== platformId));
      if (previewPlatform === platformId) {
        const remaining = selectedPlatforms.filter(p => p !== platformId);
        setPreviewPlatform(remaining[0] || '');
      }
      return;
    }

    // Check for conflicts before selecting
    const disabledReason = getPlatformDisabledReason(platformId);
    if (disabledReason) {
      toast.error(disabledReason);
      return;
    }

    // Add platform
    setSelectedPlatforms(prev => [...prev, platformId]);
    if (!previewPlatform) {
      setPreviewPlatform(platformId);
    }
  };

  // AI Text Generation - generates text respecting the smallest platform limit
  const handleGenerateText = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t('social.enterPrompt', 'Enter a prompt to generate the text'));
      return;
    }

    if (selectedPlatforms.length === 0) {
      toast.error(t('social.selectPlatformFirst', 'Select at least one platform'));
      return;
    }

    setIsGeneratingText(true);
    try {
      // Find the platform with smallest char limit to ensure text fits all
      const smallestLimitPlatform = selectedPlatforms.reduce((smallest, current) => {
        const currentLimit = platformConfig[current]?.maxLength || 2200;
        const smallestLimit = platformConfig[smallest]?.maxLength || 2200;
        return currentLimit < smallestLimit ? current : smallest;
      }, selectedPlatforms[0]);

      const charLimit = platformConfig[smallestLimitPlatform]?.maxLength || 2200;

      const response = await socialService.generateCaption({
        topic: aiPrompt,
        tone: aiTone,
        platform: smallestLimitPlatform,
        platforms: selectedPlatforms, // Send ALL selected platforms for field generation
        language: i18n.language, // Pass UI language so AI generates in correct language
        charLimit: charLimit, // Pass the actual min limit across all selected platforms
        ignorePattern,
        variationDirection: variationDirection.trim() || undefined
      });

      if (response.success && response.data) {
        let generatedText = response.data.caption;
        let generatedHashtags = response.data.hashtags || [];

        // Enforce total limit (caption + hashtags) on the frontend as safety net
        if (generatedHashtags.length > 0) {
          const hashtagsStr = generatedHashtags.join(' ');
          const totalLen = generatedText.length + 2 + hashtagsStr.length;
          if (totalLen > charLimit) {
            // Remove hashtags one by one until it fits
            while (generatedHashtags.length > 0) {
              const hStr = generatedHashtags.join(' ');
              if (generatedText.length + 2 + hStr.length <= charLimit) break;
              generatedHashtags.pop();
            }
            // If still over with no hashtags, truncate text
            if (generatedHashtags.length === 0 && generatedText.length > charLimit) {
              generatedText = generatedText.substring(0, charLimit);
            }
          }
        } else if (generatedText.length > charLimit) {
          generatedText = generatedText.substring(0, charLimit);
        }

        setContent(generatedText);
        // Fecha o autocomplete de menção: o conteúdo foi substituído pela IA, então
        // um dropdown aberto ficaria com query obsoleta sobre texto novo.
        setMentionQuery(null);

        // Set hashtags
        if (generatedHashtags.length > 0) {
          setSelectedHashtags(generatedHashtags);
        }

        // Fill platform-specific fields
        const fields = response.data.platformFields;
        if (fields) {
          // YouTube
          if (fields.youtubeTitle) setYoutubeTitle(fields.youtubeTitle);
          if (fields.youtubeTags) setYoutubeTags(fields.youtubeTags);
          // Instagram
          if (fields.instagramFirstComment) setInstagramFirstComment(fields.instagramFirstComment);
          if (fields.instagramAltText) setInstagramAltText(fields.instagramAltText);
        }

        toast.success(`Text generated (${generatedText.length}/${charLimit} chars)`);
      } else {
        throw new Error('Failed to generate text');
      }
    } catch (error) {
      console.error('Error generating text:', error);
      if (!isHandledByInterceptor(error)) {
        toast.error(t('social.generateError', 'Error generating text. Try again.'));
      }
    } finally {
      setIsGeneratingText(false);
    }
  };

  // AI Image Generation (for thumbnail)
  const handleGenerateImage = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t('social.enterPrompt', 'Enter a prompt to generate the image'));
      return;
    }

    setIsGeneratingImage(true);

    // Token so a manual thumbnail upload (or remove) during this generation wins
    // instead of being overwritten by the late AI result.
    const thumbSeq = ++thumbSeqRef.current;
    const isStale = () => thumbSeq !== thumbSeqRef.current;

    // Set thumbnail as generating
    setThumbnailItem({
      id: `ai-thumb-${thumbSeq}`,
      url: '',
      type: 'image',
      status: 'generating'
    });

    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      // Resolve optional reference image via the shared component
      let refUrl: string | null = null;
      try {
        setIsUploadingAiRef(true);
        refUrl = (await aiRefHandle.current?.resolve()) || null;
      } catch (upErr: any) {
        if (!isStale()) setThumbnailItem(null);
        toast.error(upErr?.response?.data?.message || upErr?.message || t('imageStudio.reference.uploadError', 'Failed to upload reference image.'));
        setIsGeneratingImage(false);
        setIsUploadingAiRef(false);
        return;
      } finally {
        setIsUploadingAiRef(false);
      }

      const response = await socialService.generateImage({
        prompt: aiPrompt,
        style: aiImageStyle,
        aspectRatio: aiImageRatio,
        platform: selectedPlatforms[0],
        async: true, // Use background processing
        referenceImageUrl: refUrl || undefined
      });

      let imageData = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        const result = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          120000 // 2 minute timeout
        );
        imageData = result;
      } else if (response.success && response.data) {
        imageData = response.data;
      }

      if (imageData?.url) {
        if (isStale()) return;
        setThumbnailItem({
          id: `ai-thumb-${thumbSeq}`,
          url: imageData.url,
          thumbnailUrl: imageData.thumbnailUrl,
          type: 'image',
          status: 'ready'
        });
        toast.success(t('social.imageGenerated', 'Thumbnail generated successfully!'));
        // Reset the reference image after a successful generation so subsequent
        // clicks don't accidentally keep applying it unless the user re-enables.
        aiRefHandle.current?.clear();
      } else {
        throw new Error('Failed to generate image');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      if (!isStale()) setThumbnailItem(null);
      if (!isHandledByInterceptor(error)) {
        toast.error(t('social.generateImageError', 'Error generating image. Check if the AI API is configured.'));
      }
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // ===== Carousel helpers (Instagram e Facebook) =====
  // No Facebook o carrossel sai como post com várias fotos (álbum); o limite de 10
  // é o do Instagram, mantido igual para o mesmo post servir às duas redes.
  const MAX_CAROUSEL_SLIDES = 10;

  const currentCarouselTotal = (thumbnailItem ? 1 : 0) + extraCarouselItems.length;
  const carouselSlotsLeft = Math.max(0, MAX_CAROUSEL_SLIDES - currentCarouselTotal);
  const carouselEnabled = selectedPlatforms.some(p => PLATAFORMAS_COM_CARROSSEL.includes(p));

  // Slides estruturados detectados na legenda ("Slide 1: ...", "1/5: ..." etc.)
  const detectedSlides = useMemo(() => parseSlidesFromText(content), [content]);
  // Não reoferece o banner quando os textos detectados já estão nos cards.
  const detectedSlidesApplied = !!detectedSlides &&
    coverSlideText.trim() === (detectedSlides[0] || '').trim() &&
    detectedSlides.slice(1).every((s, i) => (extraCarouselItems[i]?.slideText || '').trim() === s.trim());

  // Cria/atualiza os slides do carrossel a partir do texto detectado na legenda:
  // slide 1 vira a capa, os demais preenchem os extras existentes (na ordem) e o
  // que sobrar vira slide vazio aguardando imagem.
  const applyDetectedSlides = (slides: string[]) => {
    const capped = slides.slice(0, MAX_CAROUSEL_SLIDES);
    setCoverSlideText(capped[0] || '');
    const extraTexts = capped.slice(1);
    setExtraCarouselItems(prev => {
      const next = prev.map((it, i) => (i < extraTexts.length ? { ...it, slideText: extraTexts[i] } : it));
      for (let i = next.length; i < extraTexts.length; i++) {
        next.push({
          id: `slide-empty-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`,
          url: '',
          type: 'image',
          status: 'empty',
          slideText: extraTexts[i]
        });
      }
      return next;
    });
    toast.success(t('social.carousel.slidesApplied', {
      n: capped.length,
      defaultValue: `${capped.length} slides criados a partir do texto! Agora adicione ou gere a imagem de cada um.`
    }));
  };

  const addEmptySlide = () => {
    if (carouselSlotsLeft === 0) {
      toast.error(t('social.carousel.maxReached', 'Limite de 10 imagens no carrossel.'));
      return;
    }
    setExtraCarouselItems(prev => [...prev, {
      id: `slide-empty-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      url: '',
      type: 'image',
      status: 'empty'
    }]);
  };

  const updateSlideText = (id: string, text: string) => {
    setExtraCarouselItems(prev => prev.map(it => (it.id === id ? { ...it, slideText: text } : it)));
  };

  // Como cada rede publica um post de várias imagens — espelha o comportamento
  // real do backend (socialPublishWorker): IG = carrossel, FB = álbum, Twitter
  // corta em 4, LinkedIn = multi-imagem, Threads/Stories = só a 1ª, vídeo-only
  // não recebe imagem nenhuma.
  const multiNetworkBehavior = (p: string, n: number): string => {
    switch (p) {
      case 'instagram':
        return t('social.carousel.multi.instagram', { n, defaultValue: `carrossel com ${n} imagens` });
      case 'facebook':
        return t('social.carousel.multi.facebook', { n, defaultValue: `álbum de fotos com ${n} imagens` });
      case 'twitter':
        return n > 4
          ? t('social.carousel.multi.twitterCapped', 'somente as 4 primeiras imagens (limite do Twitter)')
          : t('social.carousel.multi.twitter', { n, defaultValue: `tweet com ${n} imagens` });
      case 'linkedin':
        return t('social.carousel.multi.linkedin', { n, defaultValue: `post com ${n} imagens` });
      case 'threads':
        return t('social.carousel.multi.firstOnly', 'somente a 1ª imagem (a capa)');
      case 'instagram_stories':
      case 'facebook_stories':
        return t('social.carousel.multi.stories', 'somente a 1ª imagem (story é mídia única)');
      case 'instagram_reels':
      case 'youtube':
      case 'youtube_shorts':
      case 'tiktok':
        return t('social.carousel.multi.needsVideo', 'exige vídeo — as imagens do carrossel não entram');
      default:
        return t('social.carousel.multi.firstOnly', 'somente a 1ª imagem (a capa)');
    }
  };

  // Upload de imagem para UM slide específico ('cover' ou id de extra) — usado
  // pelos botões dos cards de slide e disparado pelo input escondido único.
  const handleSlideFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const target = slideUploadTarget;
    e.target.value = '';
    setSlideUploadTarget(null);
    if (!file || !target) return;
    if (file.type && !file.type.startsWith('image/')) {
      toast.error(t('social.carousel.notImage', 'Arquivo ignorado: apenas imagens no carrossel.'));
      return;
    }
    if (imageTooLarge(file)) return;

    const previewUrl = URL.createObjectURL(file);

    if (target === 'cover') {
      const thumbSeq = ++thumbSeqRef.current;
      const tempId = `thumb-upload-${thumbSeq}`;
      setThumbnailItem({ id: tempId, url: previewUrl, type: 'image', status: 'uploading', progress: 0 });
      try {
        const response = await socialService.uploadImage(file, (progress) => {
          setThumbnailItem(prev => (prev && prev.id === tempId ? { ...prev, progress } : prev));
        });
        URL.revokeObjectURL(previewUrl);
        if (response.success && response.data) {
          if (thumbSeq === thumbSeqRef.current) {
            setThumbnailItem({ id: tempId, url: response.data.url, type: 'image', status: 'ready' });
          }
        } else {
          throw new Error('Upload failed');
        }
      } catch (err) {
        URL.revokeObjectURL(previewUrl);
        console.error('Cover slide upload failed:', err);
        if (thumbSeq === thumbSeqRef.current) setThumbnailItem(null);
        toast.error(t('social.carousel.uploadError', 'Erro ao subir imagem do carrossel.'));
      }
      return;
    }

    // Slide extra: troca só a imagem, preservando o texto do slide.
    setExtraCarouselItems(prev => prev.map(it =>
      it.id === target ? { ...it, url: previewUrl, status: 'uploading', progress: 0 } : it
    ));
    try {
      const response = await socialService.uploadImage(file, (progress) => {
        setExtraCarouselItems(prev => prev.map(it => (it.id === target ? { ...it, progress } : it)));
      });
      URL.revokeObjectURL(previewUrl);
      if (response.success && response.data) {
        setExtraCarouselItems(prev => prev.map(it =>
          it.id === target ? { ...it, url: response.data!.url, status: 'ready', progress: undefined } : it
        ));
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      URL.revokeObjectURL(previewUrl);
      console.error('Carousel slide upload failed:', err);
      setExtraCarouselItems(prev => prev.map(it =>
        it.id === target ? { ...it, url: '', status: 'empty', progress: undefined } : it
      ));
      toast.error(t('social.carousel.uploadError', 'Erro ao subir imagem do carrossel.'));
    }
  };

  // Gera a imagem de UM slide usando o texto daquele slide como assunto
  // (o prompt do Estúdio de IA, quando preenchido, entra como contexto do tema).
  // silent: o "Gerar com IA" em lote chama isto por slide e mostra um toast único.
  // Retorna true quando a imagem ficou pronta.
  const handleGenerateSlideImage = async (target: string, opts?: { silent?: boolean }): Promise<boolean> => {
    const isCover = target === 'cover';
    const slideText = (isCover ? coverSlideText : extraCarouselItems.find(it => it.id === target)?.slideText || '').trim();
    const fullPrompt = [aiPrompt.trim(), slideText].filter(Boolean).join('. ');
    if (!fullPrompt) {
      toast.error(t('social.carousel.needText', 'Escreva o texto do slide (ou um prompt no Estúdio de IA) para gerar a imagem.'));
      return false;
    }

    const thumbSeq = isCover ? ++thumbSeqRef.current : 0;
    if (isCover) {
      setThumbnailItem({ id: `ai-thumb-${thumbSeq}`, url: '', type: 'image', status: 'generating' });
    } else {
      setExtraCarouselItems(prev => prev.map(it => (it.id === target ? { ...it, status: 'generating' } : it)));
    }

    try {
      const { pollJobUntilComplete } = await import('../../services/aiJobService');
      const response = await socialService.generateImage({
        prompt: fullPrompt,
        style: aiImageStyle,
        aspectRatio: '1:1',
        platform: 'instagram',
        async: true
      });

      let imageData: any = null;
      if (response.async && response.jobId) {
        imageData = await pollJobUntilComplete(response.jobId, undefined, 2000, 120000);
      } else if (response.success && response.data) {
        imageData = response.data;
      }

      if (!imageData?.url) throw new Error('Failed to generate image');

      if (isCover) {
        if (thumbSeq === thumbSeqRef.current) {
          setThumbnailItem({ id: `ai-thumb-${thumbSeq}`, url: imageData.url, type: 'image', status: 'ready' });
        }
      } else {
        // Se o slide foi removido durante a geração, o map simplesmente não acha o id.
        setExtraCarouselItems(prev => prev.map(it =>
          it.id === target ? { ...it, url: imageData.url, status: 'ready' } : it
        ));
      }
      if (!opts?.silent) {
        toast.success(t('social.carousel.slideGenerated', 'Imagem do slide gerada!'));
      }
      return true;
    } catch (error) {
      console.error('Slide image generation failed:', error);
      if (isCover) {
        if (thumbSeq === thumbSeqRef.current) setThumbnailItem(null);
      } else {
        setExtraCarouselItems(prev => prev.map(it =>
          it.id === target ? { ...it, url: '', status: 'empty' } : it
        ));
      }
      if (!opts?.silent && !isHandledByInterceptor(error)) {
        toast.error(t('social.generateImageError', 'Error generating image. Check if the AI API is configured.'));
      }
      return false;
    }
  };

  // Shared image size guard — backend caps images at 10MB. Returns true (and toasts)
  // when the file is too large, so callers can bail before uploading the whole file.
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
  const imageTooLarge = (file: File): boolean => {
    if (file.size > MAX_IMAGE_SIZE) {
      const sizeMB = Math.round(file.size / (1024 * 1024));
      toast.error(t('social.imageTooLarge', {
        size: sizeMB,
        max: 10,
        defaultValue: `Imagem muito grande (${sizeMB}MB). Máximo: 10MB.`
      }));
      return true;
    }
    return false;
  };

  const handleCarouselFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';

    const takeable = Math.min(files.length, carouselSlotsLeft);
    if (takeable === 0) {
      toast.error(t('social.carousel.maxReached', 'Limite de 10 imagens no carrossel.'));
      return;
    }

    for (let i = 0; i < takeable; i++) {
      const file = files[i];
      // Reject non-images explicitly instead of silently dropping the slot.
      // (Empty file.type — some iPhone exports — is left to the backend to validate.)
      if (file.type && !file.type.startsWith('image/')) {
        toast.error(t('social.carousel.notImage', 'Arquivo ignorado: apenas imagens no carrossel.'));
        continue;
      }
      if (imageTooLarge(file)) continue;
      // Random suffix avoids id collisions when two batches land in the same millisecond.
      const tempId = `slide-upload-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`;
      const previewUrl = URL.createObjectURL(file);
      setExtraCarouselItems(prev => [...prev, {
        id: tempId,
        url: previewUrl,
        type: 'image',
        status: 'uploading',
        progress: 0
      }]);
      try {
        const response = await socialService.uploadImage(file, (progress) => {
          setExtraCarouselItems(prev => prev.map(it =>
            it.id === tempId ? { ...it, progress } : it
          ));
        });
        if (response.success && response.data) {
          URL.revokeObjectURL(previewUrl);
          setExtraCarouselItems(prev => prev.map(it =>
            it.id === tempId ? { ...it, url: response.data!.url, status: 'ready' } : it
          ));
        } else {
          throw new Error('Upload failed');
        }
      } catch (err) {
        URL.revokeObjectURL(previewUrl);
        console.error('Carousel slide upload failed:', err);
        setExtraCarouselItems(prev => prev.filter(it => it.id !== tempId));
        toast.error(t('social.carousel.uploadError', 'Erro ao subir imagem do carrossel.'));
      }
    }
  };

  const handleCarouselGenerateAi = async () => {
    // 1º caso: já existem slides com texto aguardando imagem (criados pelo banner
    // "Criar N slides" ou à mão) — gera a imagem de CADA um a partir do próprio
    // texto, em paralelo. O seletor de quantidade não se aplica aqui.
    const pendingTargets: string[] = [
      ...(!thumbnailItem && coverSlideText.trim() ? ['cover'] : []),
      ...extraCarouselItems
        .filter(it => it.status === 'empty' && (it.slideText || '').trim())
        .map(it => it.id)
    ];
    if (pendingTargets.length > 0) {
      setIsGeneratingCarouselAi(true);
      try {
        const results = await Promise.all(pendingTargets.map(tg => handleGenerateSlideImage(tg, { silent: true })));
        const ok = results.filter(Boolean).length;
        if (ok === pendingTargets.length) {
          toast.success(t('social.carousel.generated', 'Carrossel gerado!'));
        } else if (ok > 0) {
          toast(t('social.carousel.partialSuccess', 'Carrossel gerado com algumas falhas.') as string);
        } else {
          toast.error(t('social.carousel.generateError', 'Falha ao gerar carrossel.'));
        }
      } finally {
        setIsGeneratingCarouselAi(false);
      }
      return;
    }

    // 2º caso: nenhum slide pendente — gera um lote de N imagens a partir do
    // prompt do AI Studio ou, na falta dele, da própria legenda do post.
    const batchPrompt = aiPrompt.trim() || content.trim();
    if (!batchPrompt) {
      toast.error(t('social.carousel.nothingToGenerate', 'Escreva a legenda, o texto dos slides ou um prompt no AI Studio para gerar as imagens.'));
      return;
    }
    if (carouselSlotsLeft === 0) {
      toast.error(t('social.carousel.maxReached', 'Limite de 10 imagens no carrossel.'));
      return;
    }
    const desired = Math.max(2, Math.min(MAX_CAROUSEL_SLIDES, Number(carouselAiCount) || 3));

    setIsGeneratingCarouselAi(true);
    try {
      const response = await socialService.generateCarouselImages({
        prompt: batchPrompt,
        count: desired,
        style: aiImageStyle,
        platform: 'instagram'
      });
      if (!response.success || !response.data?.urls?.length) {
        throw new Error(response.message || 'no urls');
      }
      const urls = response.data.urls;
      const remaining = [...urls];

      // If there's no thumbnail yet, promote the first generated image to thumbnail
      let willHaveThumb = !!thumbnailItem;
      if (!thumbnailItem && remaining.length > 0) {
        const first = remaining.shift();
        if (first) {
          setThumbnailItem({
            id: `ai-thumb-${Date.now()}`,
            url: first,
            type: 'image',
            status: 'ready'
          });
          willHaveThumb = true;
        }
      }

      // Preenche primeiro os slides vazios (criados a partir do texto), na ordem,
      // e só depois anexa o restante — respeitando o teto de 10 slides.
      setExtraCarouselItems(prev => {
        const queue = [...remaining];
        const filled = prev.map(it => {
          if (it.status === 'empty' && queue.length > 0) {
            const url = queue.shift()!;
            return { ...it, url, status: 'ready' as const };
          }
          return it;
        });
        // `willHaveThumb` (e não o thumbnailItem do closure, que fica obsoleto após o
        // setThumbnailItem acima) — sem isso o teto liberava 1 slide a mais.
        const roomLeft = MAX_CAROUSEL_SLIDES - (willHaveThumb ? 1 : 0) - filled.length;
        const toAppend = queue.slice(0, Math.max(0, roomLeft)).map((url, i) => ({
          id: `ai-slide-${Date.now()}-${i}`,
          url,
          type: 'image' as const,
          status: 'ready' as const
        }));
        return [...filled, ...toAppend];
      });

      if (response.data.failed && response.data.failed > 0) {
        toast(t('social.carousel.partialSuccess', `Gerado com ${response.data.failed} falha(s).`) as string);
      } else {
        toast.success(t('social.carousel.generated', 'Carrossel gerado!'));
      }
    } catch (err: any) {
      console.error('Carousel AI generation failed:', err);
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        t('social.carousel.generateError', 'Falha ao gerar carrossel.')
      );
    } finally {
      setIsGeneratingCarouselAi(false);
    }
  };

  const removeCarouselSlide = (id: string) => {
    setExtraCarouselItems(prev => prev.filter(it => it.id !== id));
  };

  const moveCarouselSlide = (id: string, direction: -1 | 1) => {
    setExtraCarouselItems(prev => {
      const idx = prev.findIndex(it => it.id === id);
      if (idx === -1) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const promoteSlideToThumbnail = (id: string) => {
    // Swap an extra carousel slide into the thumbnail position (slide 1).
    // O texto acompanha a imagem: o da capa vive em coverSlideText, o dos extras
    // em item.slideText — então os dois também trocam de lugar.
    const idx = extraCarouselItems.findIndex(it => it.id === id);
    if (idx === -1) return;
    const target = extraCarouselItems[idx];
    const next = extraCarouselItems.filter((_, i) => i !== idx);
    if (thumbnailItem) {
      next.unshift({ ...thumbnailItem, slideText: coverSlideText || undefined });
    }
    setThumbnailItem({ ...target, id: `thumb-${Date.now()}`, slideText: undefined });
    setCoverSlideText(target.slideText || '');
    setExtraCarouselItems(next);
  };

  // AI Video Generation (real API)
  const handleGenerateVideo = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t('social.enterPrompt', 'Enter a prompt to generate the video'));
      return;
    }

    setIsGeneratingVideo(true);

    // Set video as generating
    setVideoItem({
      id: `ai-video-${Date.now()}`,
      url: '',
      type: 'video',
      status: 'generating'
    });

    try {
      const response = await socialService.generateVideo({
        prompt: aiPrompt,
        duration: aiVideoDuration,
        style: aiVideoStyle,
        aspectRatio: aiImageRatio,
        platform: selectedPlatforms[0]
      });

      if (response.success && response.data) {
        // For async video generation, poll for status
        if (response.data.status === 'generating') {
          const checkStatus = async () => {
            const statusResponse = await socialService.checkGenerationStatus(response.data.id);
            if (statusResponse.data.status === 'ready') {
              setVideoItem({
                id: `ai-video-${Date.now()}`,
                url: statusResponse.data.url,
                thumbnailUrl: statusResponse.data.thumbnailUrl,
                duration: statusResponse.data.duration,
                type: 'video',
                status: 'ready'
              });
              toast.success(t('social.videoGenerated', 'Video generated successfully!'));
              setIsGeneratingVideo(false);
            } else if (statusResponse.data.status === 'failed') {
              throw new Error('Video generation failed');
            } else {
              setTimeout(checkStatus, 5000);
            }
          };
          setTimeout(checkStatus, 5000);
        } else {
          setVideoItem({
            id: `ai-video-${Date.now()}`,
            url: response.data.url,
            thumbnailUrl: response.data.thumbnailUrl,
            duration: response.data.duration,
            type: 'video',
            status: 'ready'
          });
          toast.success(t('social.videoGenerated', 'Video generated successfully!'));
          setIsGeneratingVideo(false);
        }
      } else {
        throw new Error('Failed to generate video');
      }
    } catch (error) {
      console.error('Error generating video:', error);
      setVideoItem(null);
      toast.error(t('social.generateVideoError', 'Error generating video. Check if the AI API is configured.'));
      setIsGeneratingVideo(false);
    }
  };

  // Video upload handler
  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Only allow MP4 and MOV - universal formats accepted by all platforms
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'mp4' && ext !== 'mov') {
      toast.error(t('social.unsupportedVideoFormat', 'Unsupported format. Use MP4 or MOV.'));
      e.target.value = '';
      return;
    }

    // Reject empty/corrupt files up front (a 0-byte upload would create a broken
    // post / fail at S3 complete).
    if (file.size === 0) {
      toast.error(t('social.emptyVideoFile', 'O arquivo de vídeo está vazio ou corrompido.'));
      e.target.value = '';
      return;
    }

    // Enforce max video size. Uploads go through S3 multipart (chunked, direct),
    // so 2GB is comfortable; above it we reject to avoid abuse / huge S3 cost.
    const MAX_VIDEO_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
    if (file.size > MAX_VIDEO_SIZE) {
      const sizeMB = Math.round(file.size / (1024 * 1024));
      toast.error(t('social.videoTooLarge', {
        size: sizeMB,
        max: 2048,
        defaultValue: `Video is too large (${sizeMB}MB). Maximum allowed is 2GB. Please compress the video and try again.`
      }));
      e.target.value = '';
      return;
    }

    // Early heads-up: Instagram caps videos at ~1GB. If a bigger file is picked
    // with Instagram selected, warn now (non-blocking — they may be posting to
    // TikTok/Facebook too). Publishing to Instagram is hard-blocked at submit.
    const INSTAGRAM_MAX_BYTES = 1024 * 1024 * 1024; // 1GB
    if (file.size > INSTAGRAM_MAX_BYTES && selectedPlatforms.some(p => p === 'instagram' || p === 'instagram_reels')) {
      toast(t('social.videoTooLargeForInstagram', {
        defaultValue: 'O Instagram não aceita vídeos acima de 1GB. Remova o Instagram/Reels ou use um arquivo menor.'
      }), { icon: '⚠️', duration: 6000 });
    }

    // Detect video dimensions
    let videoInfo: { width: number; height: number; duration: number } | null = null;
    try {
      videoInfo = await detectVideoAndSelectPlatform(file);
      setVideoDimensions({ width: videoInfo.width, height: videoInfo.height });

      const isVertical = videoInfo.height > videoInfo.width;

      // Check if any selected platform requires vertical video
      const verticalPlatforms = ['tiktok', 'youtube_shorts', 'instagram_reels'];
      const needsVertical = selectedPlatforms.some(p => verticalPlatforms.includes(p));

      if (needsVertical && !isVertical) {
        const platformNames = selectedPlatforms
          .filter(p => verticalPlatforms.includes(p))
          .map(p => platformConfig[p]?.name)
          .join(', ');
        toast.error(t('social.videoMustBeVertical', { platforms: platformNames, defaultValue: `Video must be vertical (9:16) for: ${platformNames}` }));
        e.target.value = '';
        return;
      }

      // Check if any selected platform requires horizontal video
      const horizontalPlatforms = ['youtube'];
      const needsHorizontal = selectedPlatforms.some(p => horizontalPlatforms.includes(p));

      if (needsHorizontal && isVertical) {
        const platformNames = selectedPlatforms
          .filter(p => horizontalPlatforms.includes(p))
          .map(p => platformConfig[p]?.name)
          .join(', ');
        toast.error(t('social.videoMustBeHorizontal', { platforms: platformNames, defaultValue: `Video must be horizontal (16:9) for: ${platformNames}` }));
        e.target.value = '';
        return;
      }

    } catch (err) {
      console.warn('Could not detect video dimensions:', err);
    }

    // Keep a copy of the File so the frame picker can read it locally (avoids S3 CORS canvas taint)
    setVideoFile(file);

    // Abort any previous in-flight upload (user re-selected a file) and invalidate
    // its late callbacks.
    cancelVideoUpload();
    const uploadSeq = ++uploadSeqRef.current;
    const isStale = () => uploadSeq !== uploadSeqRef.current;
    const uploadController = new AbortController();
    uploadAbortControllerRef.current = uploadController;
    multipartAbortRef.current = null;

    // Local preview blob — must be revoked once we swap to the S3 URL (or on failure).
    const previewUrl = URL.createObjectURL(file);
    const safeDuration = typeof videoInfo?.duration === 'number' && Number.isFinite(videoInfo.duration)
      ? videoInfo.duration
      : undefined;

    setVideoItem({
      id: `upload-video-${uploadSeq}`,
      url: previewUrl,
      type: 'video',
      status: 'uploading',
      progress: 0,
      duration: safeDuration
    });

    try {
      const response = await socialService.uploadVideo(file, (progress) => {
        if (isStale()) return;
        setVideoItem(prev => prev ? { ...prev, progress } : null);
      }, {
        signal: uploadController.signal,
        onMultipartInit: (info) => {
          // If this upload was already superseded/removed while /create was in
          // flight, abort the just-created multipart right away instead of
          // leaving it for the service catch / lifecycle sweep.
          if (isStale()) { socialService.abortMultipart(info.key, info.uploadId); return; }
          multipartAbortRef.current = info;
        }
      });

      // A newer upload (or a remove) happened while this one was in flight — drop the result.
      if (isStale()) {
        URL.revokeObjectURL(previewUrl);
        return;
      }

      // Upload finished — no parts to clean up anymore for this slot.
      if (uploadAbortControllerRef.current === uploadController) {
        uploadAbortControllerRef.current = null;
        multipartAbortRef.current = null;
      }

      if (response.success && response.data) {
        URL.revokeObjectURL(previewUrl);
        setVideoItem({
          id: `upload-video-${uploadSeq}`,
          url: response.data.url,
          type: 'video',
          status: 'ready',
          duration: safeDuration,
          fileSize: file.size
        });
        toast.success(t('social.videoUploaded', 'Video uploaded successfully!'));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      // Only clear the refs if this upload is still the current one (a newer
      // upload may have replaced it and owns the refs now). The service already
      // best-effort aborted the parts on failure.
      if (uploadAbortControllerRef.current === uploadController) {
        uploadAbortControllerRef.current = null;
        multipartAbortRef.current = null;
      }
      URL.revokeObjectURL(previewUrl);
      if (isStale()) return;
      console.error('Error uploading video:', error);
      setVideoItem(null);
      setVideoFile(null);
      setVideoDimensions(null);
      toast.error(t('social.uploadError', 'Error uploading video'));
    }

    e.target.value = '';
  };

  // Detect image dimensions
  const detectImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
        URL.revokeObjectURL(img.src);
      };
      // Some files (corrupt, certain HEIC) fire neither onload nor onerror —
      // without this timeout the upload flow would hang silently before it starts.
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error('Timed out reading image metadata'));
      }, 15000);
      img.onload = () => {
        clearTimeout(timer);
        const width = img.width;
        const height = img.height;
        cleanup();
        resolve({ width, height });
      };
      img.onerror = () => {
        clearTimeout(timer);
        cleanup();
        reject(new Error('Could not read image metadata'));
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Thumbnail upload handler
  const handleThumbnailUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imageTooLarge(file)) { e.target.value = ''; return; }

    // Detect image dimensions
    try {
      const dims = await detectImageDimensions(file);
      setImageDimensions(dims);

      // Check if any selected platform requires vertical image (for stories/reels without video)
      const verticalPlatforms = ['instagram_reels', 'youtube_shorts', 'tiktok'];
      const needsVertical = selectedPlatforms.some(p => verticalPlatforms.includes(p)) && !videoItem;
      const isVertical = dims.height > dims.width;

      if (needsVertical && !isVertical) {
        const platformNames = selectedPlatforms
          .filter(p => verticalPlatforms.includes(p))
          .map(p => platformConfig[p]?.name)
          .join(', ');
        toast.error(t('social.imageMustBeVertical', { platforms: platformNames, defaultValue: `Image must be vertical (9:16) for: ${platformNames}` }));
        e.target.value = '';
        return;
      }
    } catch (err) {
      console.warn('Could not detect image dimensions:', err);
    }

    // Invalidate any previous in-flight thumbnail upload/generation, and track the blob.
    const thumbSeq = ++thumbSeqRef.current;
    const isStale = () => thumbSeq !== thumbSeqRef.current;
    const previewUrl = URL.createObjectURL(file);

    // Set uploading state
    setThumbnailItem({
      id: `upload-thumb-${thumbSeq}`,
      url: previewUrl,
      type: 'image',
      status: 'uploading',
      progress: 0
    });

    try {
      const response = await socialService.uploadImage(file, (progress) => {
        if (isStale()) return;
        setThumbnailItem(prev => prev ? { ...prev, progress } : null);
      });

      if (isStale()) {
        URL.revokeObjectURL(previewUrl);
        return;
      }

      if (response.success && response.data) {
        URL.revokeObjectURL(previewUrl);
        setThumbnailItem({
          id: `upload-thumb-${thumbSeq}`,
          url: response.data.url,
          type: 'image',
          status: 'ready'
        });
        toast.success(t('social.imageUploaded', 'Thumbnail uploaded successfully!'));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      URL.revokeObjectURL(previewUrl);
      if (isStale()) return;
      console.error('Error uploading thumbnail:', error);
      setThumbnailItem(null);
      setImageDimensions(null);
      toast.error(t('social.uploadError', 'Error uploading thumbnail'));
    }

    e.target.value = '';
  };

  // Remove video
  const removeVideo = () => {
    // Invalidate any in-flight upload so a late completion can't re-add the video,
    // and abort its multipart parts so they don't linger in the bucket.
    uploadSeqRef.current++;
    cancelVideoUpload();
    setVideoItem(null);
    setVideoFile(null);
    setVideoDimensions(null);
    // When the post was loaded as a reels/video (video + accompanying thumb),
    // clearing the video should also drop the thumbnail that belonged to it —
    // otherwise users see an orphan image and think "the video came back".
    // Only drop thumb if the composer is purely for reels (no feed platform like
    // instagram/facebook that uses a standalone image), or if the thumb clearly
    // originated from the editingPost's media_urls alongside the video.
    const isPureVideoFlow = selectedPlatforms.every(p =>
      ['instagram_reels', 'youtube_shorts', 'tiktok', 'youtube'].includes(p)
    );
    if (isPureVideoFlow || thumbnailItem?.id?.startsWith('existing-thumb')) {
      setThumbnailItem(null);
      setImageDimensions(null);
    }
  };

  // Remove thumbnail
  const removeThumbnail = () => {
    // Invalidate any in-flight thumbnail upload/generation so it can't re-add itself.
    thumbSeqRef.current++;
    setThumbnailItem(null);
    setImageDimensions(null);
  };

  // Logo upload handler
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imageTooLarge(file)) { e.target.value = ''; return; }

    const logoSeq = ++logoSeqRef.current;
    const isStale = () => logoSeq !== logoSeqRef.current;
    const previewUrl = URL.createObjectURL(file);

    // Set uploading state
    setLogoItem({
      id: `upload-logo-${logoSeq}`,
      url: previewUrl,
      type: 'image',
      status: 'uploading',
      progress: 0
    });

    try {
      const response = await socialService.uploadImage(file, (progress) => {
        if (isStale()) return;
        setLogoItem(prev => prev ? { ...prev, progress } : null);
      });

      if (isStale()) {
        URL.revokeObjectURL(previewUrl);
        return;
      }

      if (response.success && response.data) {
        URL.revokeObjectURL(previewUrl);
        setLogoItem({
          id: `upload-logo-${logoSeq}`,
          url: response.data.url,
          type: 'image',
          status: 'ready'
        });
        setAddLogoToThumbnail(true); // Auto-enable when logo is uploaded
        toast.success(t('social.logoUploaded', 'Logo uploaded successfully!'));
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      URL.revokeObjectURL(previewUrl);
      if (isStale()) return;
      console.error('Error uploading logo:', error);
      setLogoItem(null);
      toast.error(t('social.uploadError', 'Error uploading logo'));
    }

    e.target.value = '';
  };

  // Remove logo
  const removeLogo = () => {
    logoSeqRef.current++;
    setLogoItem(null);
    setAddLogoToThumbnail(false);
  };

  // Check if any selected platform requires video
  const requiresVideo = selectedPlatforms.some(p => platformConfig[p]?.requiresVideo);
  // Check if any selected platform requires thumbnail
  const requiresThumbnail = selectedPlatforms.some(p => platformConfig[p]?.requiresThumbnail);

  const toggleHashtag = (hashtag: string) => {
    const tag = hashtag.startsWith('#') ? hashtag : `#${hashtag}`;
    setSelectedHashtags(prev =>
      prev.includes(tag)
        ? prev.filter(h => h !== tag)
        : [...prev, tag]
    );
  };

  const addCustomHashtag = () => {
    if (hashtagInput.trim()) {
      const tag = hashtagInput.startsWith('#') ? hashtagInput.trim() : `#${hashtagInput.trim()}`;
      if (!selectedHashtags.includes(tag)) {
        setSelectedHashtags(prev => [...prev, tag]);
      }
      setHashtagInput('');
    }
  };

  const handleSubmit = (status: 'draft' | 'scheduled' | 'publish_now') => {
    // Validation: check character limits per platform
    const finalText = buildFinalContent(content);

    const exceededPlatforms = selectedPlatforms
      .filter(p => {
        const limit = platformConfig[p]?.maxLength;
        return limit && finalText.length > limit;
      })
      .map(p => `${platformConfig[p]?.name} (${finalText.length}/${platformConfig[p]?.maxLength})`);

    if (exceededPlatforms.length > 0) {
      toast.error(t('social.contentTooLong', `Text exceeds character limit for: ${exceededPlatforms.join(', ')}`));
      return;
    }

    // Validation: block posting/scheduling to a platform whose account isn't
    // connected. Otherwise a scheduled post is stored and only fails days later
    // at publish time — and editing a post can keep a since-disconnected platform
    // selected. (Skip when we have no account data at all — handled elsewhere.)
    if (status !== 'draft' && connectedAccounts.length > 0) {
      const disconnected = selectedPlatforms.filter(p => {
        const linkedTo = linkedPlatforms[p];
        return !connectedAccounts.some(a => (a.platform === p || a.platform === linkedTo) && a.connected);
      });
      if (disconnected.length > 0) {
        const names = disconnected.map(p => platformConfig[p]?.name || p).join(', ');
        toast.error(t('social.platformsNotConnected', {
          platforms: names,
          defaultValue: `Estas redes não estão conectadas e o post falharia: ${names}. Conecte-as ou remova-as.`
        }));
        return;
      }
    }

    // Validation: block submit while attached media is still uploading/generating —
    // otherwise the media is silently dropped from the post (it only gets included
    // when status === 'ready'), and the user thinks they posted with a video.
    if (videoItem && videoItem.status !== 'ready') {
      toast.error(t('social.videoStillUploading', 'Aguarde o upload do vídeo terminar antes de publicar.'));
      return;
    }
    if (thumbnailItem && thumbnailItem.status !== 'ready') {
      toast.error(t('social.thumbnailStillUploading', 'Aguarde o upload da imagem terminar antes de publicar.'));
      return;
    }
    if (extraCarouselItems.some(it => it.status === 'uploading' || it.status === 'generating')) {
      toast.error(t('social.carouselStillUploading', 'Aguarde o upload das imagens do carrossel terminar antes de publicar.'));
      return;
    }

    // Carrossel: todo slide precisa de imagem para publicar/agendar (o Instagram
    // rejeita slide sem mídia). Rascunho pode salvar — os textos ficam guardados
    // em metadata e os slides vazios voltam ao reabrir.
    if (status !== 'draft' && carouselEnabled) {
      const emptyIdx = extraCarouselItems.findIndex(it => it.status === 'empty');
      if (emptyIdx !== -1) {
        toast.error(t('social.carousel.missingImage', {
          n: emptyIdx + 2,
          defaultValue: `O slide ${emptyIdx + 2} do carrossel está sem imagem. Adicione ou gere uma imagem, ou remova o slide.`
        }));
        return;
      }
      if (extraCarouselItems.length > 0 && (!thumbnailItem || thumbnailItem.status !== 'ready')) {
        toast.error(t('social.carousel.missingCover', 'Adicione a imagem de capa (slide 1) do carrossel.'));
        return;
      }
    }

    // Validation: don't allow scheduling in the past.
    if (status === 'scheduled') {
      const utc = localDatetimeToUTC(scheduledAt);
      const when = utc ? new Date(utc) : null;
      if (!when || isNaN(when.getTime())) {
        toast.error(t('social.invalidScheduleDate', 'Escolha uma data de agendamento válida.'));
        return;
      }
      if (when.getTime() < Date.now()) {
        toast.error(t('social.scheduleInPast', 'A data de agendamento deve estar no futuro.'));
        return;
      }
      // The publisher cron runs once a minute, so scheduling less than ~2 min
      // ahead is unreliable and usually means the user wanted to post now.
      if (when.getTime() < Date.now() + 2 * 60 * 1000) {
        toast.error(t('social.scheduleTooSoon', 'Agende pelo menos 2 minutos à frente, ou use "Publicar agora".'));
        return;
      }
    }

    // Validation: check if video is required but not present
    if (requiresVideo && (!videoItem || videoItem.status !== 'ready')) {
      const videoPlatforms = selectedPlatforms
        .filter(p => platformConfig[p]?.requiresVideo)
        .map(p => platformConfig[p]?.name)
        .join(', ');
      toast.error(t('social.videoRequired', `Video required for: ${videoPlatforms}`));
      return;
    }

    // Validation: per-platform video duration limits. The reel limit was raised
    // to 15 min (900s); without this check a too-long video would upload fine and
    // only fail later at the platform API, landing the post in failed/partial.
    // TikTok is enforced separately below via its account-specific creator-info max.
    if (videoItem?.duration) {
      const MAX_VIDEO_SECONDS: Record<string, number> = {
        instagram: 900,
        instagram_reels: 900,
        instagram_stories: 60,
        facebook_stories: 60,
        youtube_shorts: 180,
        twitter: 140,
        threads: 300,
        linkedin: 600,
      };
      const tooLongPlatform = selectedPlatforms.find(
        p => MAX_VIDEO_SECONDS[p] && videoItem.duration! > MAX_VIDEO_SECONDS[p]
      );
      if (tooLongPlatform) {
        const max = MAX_VIDEO_SECONDS[tooLongPlatform];
        const limitLabel = max >= 60 ? `${Math.floor(max / 60)} min` : `${max}s`;
        const platformName = platformConfig[tooLongPlatform]?.name || tooLongPlatform;
        toast.error(t('social.videoTooLongForPlatform', {
          platform: platformName,
          limit: limitLabel,
          defaultValue: `O vídeo é muito longo para ${platformName} (máximo ${limitLabel}).`
        }));
        return;
      }
    }

    // Validation: Instagram won't accept videos over ~1GB — block before
    // publishing so the post doesn't fail at the platform. Other platforms
    // (TikTok/Facebook) accept up to the app's 2GB cap.
    const INSTAGRAM_MAX_BYTES = 1024 * 1024 * 1024; // 1GB
    if (videoItem?.fileSize && videoItem.fileSize > INSTAGRAM_MAX_BYTES
        && selectedPlatforms.some(p => p === 'instagram' || p === 'instagram_reels')) {
      toast.error(t('social.videoTooLargeForInstagram', {
        defaultValue: 'O Instagram não aceita vídeos acima de 1GB. Remova o Instagram/Reels ou use um arquivo menor.'
      }));
      return;
    }

    // Validation: check if thumbnail is required but not present
    if (requiresThumbnail && (!thumbnailItem || thumbnailItem.status !== 'ready')) {
      const thumbPlatforms = selectedPlatforms
        .filter(p => platformConfig[p]?.requiresThumbnail)
        .map(p => platformConfig[p]?.name)
        .join(', ');
      toast.error(t('social.thumbnailRequired', `Thumbnail required for: ${thumbPlatforms}`));
      return;
    }

    // Validation: TikTok composer compliance (Content Sharing Developer Guidelines)
    if (selectedPlatforms.includes('tiktok')) {
      if (!tiktokPrivacyLevel) {
        toast.error(t('social.tiktokPrivacyRequired', 'Choose who can view your TikTok video before posting.'));
        return;
      }
      if (tiktokDiscloseEnabled && !tiktokBrandOrganic && !tiktokBrandContent) {
        toast.error(t('social.tiktokDiscloseChoice', 'You enabled the disclosure — pick "Your Brand" or "Branded Content".'));
        return;
      }
      if (tiktokDiscloseEnabled && tiktokBrandContent && tiktokPrivacyLevel === 'SELF_ONLY') {
        toast.error(t('social.tiktokBrandedSelfOnly', 'Branded Content cannot be posted as Only Me on TikTok. Switch the privacy or turn off Branded Content.'));
        return;
      }
      if (!tiktokMusicConfirmed) {
        toast.error(t('social.tiktokMusicRequired', 'Acknowledge TikTok\'s Music Usage Confirmation before posting.'));
        return;
      }
      if (videoItem?.duration && tiktokCreatorInfo?.maxVideoPostDurationSec
          && videoItem.duration > tiktokCreatorInfo.maxVideoPostDurationSec) {
        toast.error(t('social.tiktokVideoTooLong', `TikTok allows videos up to ${tiktokCreatorInfo.maxVideoPostDurationSec}s for this account.`));
        return;
      }
    }

    const finalContent = buildFinalContent(content);

    // Build media_urls array: video first, then thumbnail, then carousel extras (IG), then logo.
    // Logo is skipped when the user has built a carousel so it does not end up as one of the slides.
    const mediaUrls: string[] = [];
    if (videoItem?.status === 'ready' && videoItem.url) {
      mediaUrls.push(videoItem.url);
    }
    if (thumbnailItem?.status === 'ready' && thumbnailItem.url) {
      mediaUrls.push(thumbnailItem.url);
    }
    const readyCarouselExtras = extraCarouselItems
      .filter(it => it?.status === 'ready' && it.url)
      .map(it => it.url);
    const isCarouselSubmit = readyCarouselExtras.length > 0 && carouselEnabled;
    if (isCarouselSubmit) {
      mediaUrls.push(...readyCarouselExtras);
    }
    if (!isCarouselSubmit && logoItem?.status === 'ready' && logoItem.url && addLogoToThumbnail) {
      mediaUrls.push(logoItem.url);
    }

    // Build platform-specific metadata
    const metadata: PostData['metadata'] = {};

    // YouTube fields
    if (selectedPlatforms.includes('youtube') || selectedPlatforms.includes('youtube_shorts')) {
      metadata.youtube = {
        title: youtubeTitle || undefined,
        tags: youtubeTags || undefined,
        category: youtubeCategory || undefined,
        visibility: youtubeVisibility || undefined
      };
    }

    // Instagram fields (also applies to instagram_reels)
    if (selectedPlatforms.includes('instagram') || selectedPlatforms.includes('instagram_reels')) {
      const igMeta: any = {
        alt_text: instagramAltText || undefined,
        first_comment: instagramFirstComment || undefined
      };
      // Textos dos slides do carrossel (índice 0 = capa) — persistidos para o
      // round-trip de edição/rascunho. Só entram quando algum slide tem texto.
      const slideTexts = [coverSlideText, ...extraCarouselItems.map(it => it.slideText || '')];
      if (selectedPlatforms.includes('instagram') && slideTexts.some(s => s.trim())) {
        igMeta.carousel_slides = slideTexts;
      }
      // Marcar contas NA FOTO (user_tags) — só vale para posts de imagem do feed.
      if (igUserTags.length > 0) {
        igMeta.user_tags = igUserTags;
      }
      // Trial Reel is only available when Instagram Reels is explicitly selected
      if (trialReelEnabled && selectedPlatforms.includes('instagram_reels')) {
        igMeta.trial_reel = {
          enabled: true,
          graduation_strategy: trialGraduationStrategy
        };
      }
      metadata.instagram = igMeta;
    }

    // Carrossel só de Facebook: sem Instagram no post, os textos dos slides ficam em
    // metadata.facebook para o rascunho reabrir com o roteiro.
    if (selectedPlatforms.includes('facebook') && !selectedPlatforms.includes('instagram')) {
      const slideTexts = [coverSlideText, ...extraCarouselItems.map(it => it.slideText || '')];
      if (slideTexts.some(s => s.trim())) {
        metadata.facebook = { carousel_slides: slideTexts };
      }
    }

    // LinkedIn fields
    if (selectedPlatforms.includes('linkedin')) {
      metadata.linkedin = {
        article_mode: linkedinArticleMode || undefined
      };
    }

    // Twitter fields
    if (selectedPlatforms.includes('twitter')) {
      metadata.twitter = {
        thread_mode: twitterThreadMode || undefined
      };
    }

    // TikTok fields — every value must come from a deliberate user choice.
    // Submit is gated by the validation block above (privacy required, branded
    // content + SELF_ONLY forbidden) so we can safely send what state holds.
    if (selectedPlatforms.includes('tiktok')) {
      metadata.tiktok = {
        privacy_level: tiktokPrivacyLevel || undefined,
        comment_enabled: tiktokCommentEnabled,
        duet_enabled: tiktokDuetEnabled,
        stitch_enabled: tiktokStitchEnabled,
        brand_organic_toggle: tiktokDiscloseEnabled && tiktokBrandOrganic,
        brand_content_toggle: tiktokDiscloseEnabled && tiktokBrandContent
      };
    }

    // Logo fields
    if (logoItem?.status === 'ready' && addLogoToThumbnail) {
      metadata.logo = {
        url: logoItem.url,
        position: logoPosition,
        size: logoSize,
        enabled: true
      };
    }

    onSubmit({
      content: finalContent,
      platforms: selectedPlatforms,
      // Publish-now explicitly strips the scheduled date so the backend publishes immediately,
      // even when the user had a date filled in from a previously-scheduled post.
      scheduled_at: status === 'publish_now' ? undefined : localDatetimeToUTC(scheduledAt),
      media_urls: mediaUrls,
      hashtags: selectedHashtags,
      status,
      // Marca o post como carrossel — o worker já publica como CAROUSEL pela
      // contagem de imagens, mas o content_type mantém listagens/filtros corretos.
      ...(isCarouselSubmit ? { content_type: 'carousel' as const } : {}),
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-start sm:items-center justify-center p-0 sm:p-4">
        <div className="relative w-full max-w-5xl bg-white dark:bg-gray-800 sm:rounded-2xl shadow-2xl overflow-hidden sm:my-4 min-h-screen sm:min-h-0">
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
            <h2 className="text-base sm:text-xl font-semibold text-gray-900 dark:text-white">
              {editingPost ? t('social.editPost', 'Edit Post') : t('social.newPost', 'New Post')}
            </h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-col lg:flex-row lg:max-h-[80vh] lg:overflow-hidden">
            {/* Editor Section */}
            <div className="flex-1 p-4 sm:p-6 space-y-5 lg:overflow-y-auto">
              {/* Proactive warning when the current project has NO connected account —
                  otherwise the user publishes and the post silently ends up as a draft.
                  Non-blocking: it only informs, never prevents anything. */}
              {!connectedAccounts.some(a => a.connected) && (
                <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800 dark:text-amber-200">
                    {t('social.noAccountsInProjectWarning', 'Este projeto não tem nenhuma rede social conectada. Conecte uma conta em Configurações, ou troque de projeto no canto superior esquerdo — senão o post ficará como rascunho.')}
                  </div>
                </div>
              )}

              {/* Platform Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('social.selectPlatforms', 'Select platforms')}
                </label>

                {/* Platform groups */}
                <div className="space-y-3">
                  {/* Flexible platforms (any format) */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">
                      {t('social.flexiblePlatforms', 'Flexible (any format)')}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {platforms.filter(p => platformGroups.flexible.includes(p.id)).map((platform) => {
                        const IconComponent = PlatformIcons[platform.id];
                        const isSelected = selectedPlatforms.includes(platform.id);
                        const disabledReason = !isSelected ? getPlatformDisabledReason(platform.id) : null;
                        const isDisabled = !platform.connected || !!disabledReason;

                        return (
                          <button
                            key={platform.id}
                            onClick={() => !isDisabled && togglePlatform(platform.id)}
                            disabled={isDisabled}
                            title={disabledReason || (!platform.connected ? t('social.notConnected', 'Not connected') : '')}
                            className={`
                              flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-sm relative
                              ${isDisabled
                                ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                                : isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              }
                            `}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white bg-gradient-to-br ${platform.color}`}>
                              {IconComponent && <IconComponent className="h-3 w-3" />}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white hidden sm:inline">
                              {platform.name}
                            </span>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            )}
                            {disabledReason && (
                              <AlertCircle className="h-3 w-3 text-amber-500 absolute -top-1 -right-1" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Vertical video platforms */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">
                      {t('social.verticalPlatforms', 'Vertical Video (9:16)')}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {platforms.filter(p => platformGroups.vertical.includes(p.id)).map((platform) => {
                        const IconComponent = PlatformIcons[platform.id];
                        const isSelected = selectedPlatforms.includes(platform.id);
                        const disabledReason = !isSelected ? getPlatformDisabledReason(platform.id) : null;
                        const isDisabled = !platform.connected || !!disabledReason;

                        return (
                          <button
                            key={platform.id}
                            onClick={() => !isDisabled && togglePlatform(platform.id)}
                            disabled={isDisabled}
                            title={disabledReason || (!platform.connected ? t('social.notConnected', 'Not connected') : '')}
                            className={`
                              flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-sm relative
                              ${isDisabled
                                ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                                : isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              }
                            `}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white bg-gradient-to-br ${platform.color}`}>
                              {IconComponent && <IconComponent className="h-3 w-3" />}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white hidden sm:inline">
                              {platform.name}
                            </span>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            )}
                            {disabledReason && (
                              <AlertCircle className="h-3 w-3 text-amber-500 absolute -top-1 -right-1" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Horizontal video platforms */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">
                      {t('social.horizontalPlatforms', 'Horizontal Video (16:9)')}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {platforms.filter(p => platformGroups.horizontal.includes(p.id)).map((platform) => {
                        const IconComponent = PlatformIcons[platform.id];
                        const isSelected = selectedPlatforms.includes(platform.id);
                        const disabledReason = !isSelected ? getPlatformDisabledReason(platform.id) : null;
                        const isDisabled = !platform.connected || !!disabledReason;

                        return (
                          <button
                            key={platform.id}
                            onClick={() => !isDisabled && togglePlatform(platform.id)}
                            disabled={isDisabled}
                            title={disabledReason || (!platform.connected ? t('social.notConnected', 'Not connected') : '')}
                            className={`
                              flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-sm relative
                              ${isDisabled
                                ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                                : isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              }
                            `}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white bg-gradient-to-br ${platform.color}`}>
                              {IconComponent && <IconComponent className="h-3 w-3" />}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white hidden sm:inline">
                              {platform.name}
                            </span>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            )}
                            {disabledReason && (
                              <AlertCircle className="h-3 w-3 text-amber-500 absolute -top-1 -right-1" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Stories (somem em 24h, mídia única 9:16) */}
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">
                      {t('social.storiesPlatforms', 'Stories (9:16, expiram em 24h)')}
                    </span>
                    <div className="flex flex-wrap gap-2">
                      {platforms.filter(p => platformGroups.stories.includes(p.id)).map((platform) => {
                        const IconComponent = PlatformIcons[platform.id];
                        const isSelected = selectedPlatforms.includes(platform.id);
                        const disabledReason = !isSelected ? getPlatformDisabledReason(platform.id) : null;
                        const isDisabled = !platform.connected || !!disabledReason;

                        return (
                          <button
                            key={platform.id}
                            onClick={() => !isDisabled && togglePlatform(platform.id)}
                            disabled={isDisabled}
                            title={disabledReason || (!platform.connected ? t('social.notConnected', 'Not connected') : '')}
                            className={`
                              flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all text-sm relative
                              ${isDisabled
                                ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                                : isSelected
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                              }
                            `}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white bg-gradient-to-br ${platform.color}`}>
                              {IconComponent && <IconComponent className="h-3 w-3" />}
                            </div>
                            <span className="font-medium text-gray-900 dark:text-white hidden sm:inline">
                              {platform.name}
                            </span>
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                            )}
                            {disabledReason && (
                              <AlertCircle className="h-3 w-3 text-amber-500 absolute -top-1 -right-1" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {connectedAccounts.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-3">
                    {t('social.noAccountsWarning', 'Connect your accounts in the "Accounts" tab to publish')}
                  </p>
                )}

                {/* Conflict warning */}
                {selectedPlatforms.length > 0 && (
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    {selectedPlatforms.some(p => platformGroups.vertical.includes(p)) && (
                      <p className="flex items-center gap-1">
                        <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                        {t('social.verticalVideoNote', 'Vertical platforms require 9:16 video')}
                      </p>
                    )}
                    {selectedPlatforms.some(p => platformGroups.horizontal.includes(p)) && (
                      <p className="flex items-center gap-1 mt-1">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        {t('social.horizontalVideoNote', 'YouTube requires horizontal 16:9 video')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Aviso: menção/marcação NÃO funciona em Stories (limite da API da Meta) */}
              {(selectedPlatforms.includes('instagram_stories') || selectedPlatforms.includes('facebook_stories')) && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    {t('social.storyMentionUnsupported', 'Menção não é suportada em Stories pela API da Meta — a legenda é ignorada e não há figurinha de menção via API. Para mencionar em um Story, adicione a figurinha manualmente no app do Instagram.')}
                  </p>
                </div>
              )}

              {/* Brand context banner — shows which BusinessProfile is guiding the AI */}
              {brandProfileLoaded && (
                brandProfile?.business_name ? (
                  <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-purple-50/70 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white shrink-0">
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-100">
                        {t('social.brandContext.title', 'Gerando com base em')}{' '}
                        <strong className="font-semibold">{brandProfile.business_name}</strong>
                        {brandProfile.brand_voice && (
                          <span className="text-gray-500 dark:text-gray-400"> · {brandProfile.brand_voice}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {ignorePattern
                          ? t('social.brandContext.ignoringPattern', 'Ignorando o padrão dos posts anteriores neste post.')
                          : t('social.brandContext.followingPattern', 'A IA vai olhar seus últimos posts para manter o padrão.')}
                      </p>
                    </div>
                    <RouterLink
                      to="/settings/business-profile"
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-purple-300 dark:border-purple-700 text-purple-700 dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-purple-900/40 transition"
                      title={t('social.brandContext.editTooltip', 'Editar perfil do negócio') as string}
                    >
                      <SettingsIcon className="w-3.5 h-3.5" />
                      {t('social.brandContext.edit', 'Editar')}
                    </RouterLink>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50/70 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center text-white shrink-0">
                      <Building2 className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-100">
                        {t('social.brandContext.missing', 'Conte à IA sobre o seu negócio para gerar posts melhores.')}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t('social.brandContext.missingHint', 'Sem o perfil, a geração usa só o prompt atual.')}
                      </p>
                    </div>
                    <RouterLink
                      to="/settings/business-profile"
                      className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition"
                    >
                      <SettingsIcon className="w-3.5 h-3.5" />
                      {t('social.brandContext.configure', 'Configurar')}
                    </RouterLink>
                  </div>
                )
              )}

              {/* AI Generation Panel */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800 overflow-hidden">
                <button
                  onClick={() => setShowAIPanel(!showAIPanel)}
                  className="w-full flex items-center justify-between p-4 hover:bg-purple-100/50 dark:hover:bg-purple-900/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {t('social.aiStudio', 'AI Studio')}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('social.aiStudioDesc', 'Generate text, images and videos with AI')}
                      </p>
                    </div>
                  </div>
                  <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${showAIPanel ? 'rotate-180' : ''}`} />
                </button>

                {showAIPanel && (
                  <div className="p-4 pt-0 space-y-4 border-t border-purple-200 dark:border-purple-800">
                    {/* AI Prompt */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                        {t('social.aiPrompt', 'Describe what you want to create')}
                      </label>
                      <textarea
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder={t('social.aiPromptPlaceholder', 'E.g.: A post about digital marketing tips for small businesses...')}
                        className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                        rows={2}
                      />
                    </div>

                    {/* AI Options Grid */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('social.tone', 'Tone')}
                        </label>
                        <select
                          value={aiTone}
                          onChange={(e) => setAiTone(e.target.value)}
                          className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="professional">{t('social.tones.professional', 'Professional')}</option>
                          <option value="casual">{t('social.tones.casual', 'Casual')}</option>
                          <option value="friendly">{t('social.tones.friendly', 'Friendly')}</option>
                          <option value="humorous">{t('social.tones.humorous', 'Humorous')}</option>
                          <option value="inspirational">{t('social.tones.inspirational', 'Inspirational')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('social.imageStyle', 'Image Style')}
                        </label>
                        <select
                          value={aiImageStyle}
                          onChange={(e) => setAiImageStyle(e.target.value as any)}
                          className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="professional">{t('social.imageStyles.professional', 'Professional')}</option>
                          <option value="realistic">{t('social.imageStyles.realistic', 'Realistic')}</option>
                          <option value="artistic">{t('social.imageStyles.artistic', 'Artistic')}</option>
                          <option value="minimal">{t('social.imageStyles.minimal', 'Minimal')}</option>
                          <option value="vibrant">{t('social.imageStyles.vibrant', 'Vibrant')}</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('social.aspectRatio', 'Aspect Ratio')}
                        </label>
                        <select
                          value={aiImageRatio}
                          onChange={(e) => setAiImageRatio(e.target.value as any)}
                          className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="1:1">{t('social.aspectRatios.1:1', '1:1 (Feed)')}</option>
                          <option value="4:5">{t('social.aspectRatios.4:5', '4:5 (Instagram)')}</option>
                          <option value="16:9">{t('social.aspectRatios.16:9', '16:9 (YouTube)')}</option>
                          <option value="9:16">{t('social.aspectRatios.9:16', '9:16 (Stories/Reels)')}</option>
                        </select>
                      </div>
                    </div>

                    {/* Variation controls — only useful when a brand profile exists */}
                    {brandProfile?.business_name && (
                      <div className="rounded-lg bg-white/60 dark:bg-gray-900/40 border border-purple-200 dark:border-purple-800 p-3 space-y-2">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={ignorePattern}
                            onChange={(e) => setIgnorePattern(e.target.checked)}
                            className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                          />
                          <div>
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {t('social.variation.ignore', 'Fugir do padrão dos posts anteriores')}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {t('social.variation.ignoreHint', 'Ative quando quiser um post diferente do seu estilo habitual nesta geração.')}
                            </p>
                          </div>
                        </label>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                            {t('social.variation.direction', 'Direção desta variação (opcional)')}
                          </label>
                          <input
                            type="text"
                            value={variationDirection}
                            onChange={(e) => setVariationDirection(e.target.value)}
                            placeholder={t('social.variation.directionPlaceholder', 'Ex.: mais descontraído, com humor; ou: tom mais técnico dessa vez') as string}
                            className="w-full px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                            maxLength={400}
                          />
                        </div>
                      </div>
                    )}

                    {/* Reference image (optional) — applies to "Generate Image" only */}
                    <AIImageReferenceInput
                      ref={aiRefHandle}
                      uploadReference={async (f) => {
                        const { imageStudioService } = await import('../../services/imageStudioService');
                        return await imageStudioService.uploadReference(f);
                      }}
                    />

                    {/* AI Generation Buttons */}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleGenerateText}
                        disabled={isGeneratingText || !aiPrompt.trim()}
                        className="gap-2"
                      >
                        {isGeneratingText ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Wand2 className="h-4 w-4" />
                        )}
                        {t('social.generateText', 'Generate Text')}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={handleGenerateImage}
                        disabled={isGeneratingImage || isUploadingAiRef || !aiPrompt.trim()}
                        className="gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0 hover:from-purple-600 hover:to-pink-600"
                      >
                        {isGeneratingImage || isUploadingAiRef ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ImagePlus className="h-4 w-4" />
                        )}
                        {isUploadingAiRef
                          ? t('imageStudio.reference.uploading', 'Subindo imagem de referência...')
                          : t('social.generateImage', 'Generate Image')}
                      </Button>
                      {/* Video generation hidden - API not implemented */}
                    </div>
                    {/* Custo de IA de cada botão, saldo do mês e recarga sem sair do post */}
                    <div className="space-y-1">
                      <AiCostBadge feature="social" action="generate-caption" />
                      <AiCostBadge feature="ai-generation" action="content-image" unitLabel={t('aiCredits.perImage', 'por imagem')} />
                    </div>
                  </div>
                )}
              </div>

              {/* Carousel (Instagram e Facebook) */}
              {carouselEnabled && (
                <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10 p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shrink-0">
                      <Layers className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                        {t('social.carousel.title', 'Carrossel (Instagram e Facebook)')}
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {t('social.carousel.hint', 'De 2 a 10 slides, cada um com a própria imagem. O slide 1 é a capa.')}
                      </p>
                    </div>
                    <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400 font-medium">
                      {currentCarouselTotal}/{MAX_CAROUSEL_SLIDES}
                    </span>
                  </div>

                  {/* Banner: a legenda tem slides ("Slide 1 / Slide 2...") — oferece criar os cards */}
                  {detectedSlides && !detectedSlidesApplied && (
                    <div className="flex flex-wrap items-center gap-2 mb-3 p-3 rounded-lg border border-purple-300 dark:border-purple-700 bg-purple-100/70 dark:bg-purple-900/30">
                      <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-300 shrink-0" />
                      <p className="flex-1 min-w-[180px] text-xs text-purple-900 dark:text-purple-100">
                        {t('social.carousel.detected', {
                          n: detectedSlides.length,
                          defaultValue: `Seu texto está dividido em ${detectedSlides.length} slides. Crie os slides e atribua uma imagem a cada um.`
                        })}
                      </p>
                      <button
                        type="button"
                        onClick={() => applyDetectedSlides(detectedSlides)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium hover:from-purple-600 hover:to-pink-600"
                      >
                        <Layers className="w-3.5 h-3.5" />
                        {t('social.carousel.createSlides', {
                          n: detectedSlides.length,
                          defaultValue: `Criar ${detectedSlides.length} slides`
                        })}
                      </button>
                    </div>
                  )}

                  {/* Cards de slide: capa (slide 1) + extras (2..N), cada um com imagem + texto */}
                  {(thumbnailItem || extraCarouselItems.length > 0 || coverSlideText.trim()) && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                      {/* Slide 1 — capa (usa o slot de imagem principal / thumbnailItem) */}
                      <div className="rounded-lg border border-purple-300 dark:border-purple-700 bg-white dark:bg-gray-800 overflow-hidden">
                        <div className="relative aspect-square bg-gray-100 dark:bg-gray-900 group">
                          {thumbnailItem?.status === 'ready' && thumbnailItem.url ? (
                            <>
                              <img src={thumbnailItem.url} alt="slide-1" className="w-full h-full object-cover" />
                              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => { setSlideUploadTarget('cover'); slideReplaceInputRef.current?.click(); }}
                                  title={t('social.carousel.replaceImage', 'Trocar imagem') as string}
                                  className="p-1.5 bg-white/90 rounded text-gray-700 hover:bg-white"
                                >
                                  <Upload className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleGenerateSlideImage('cover')}
                                  title={t('social.carousel.generateImage', 'Gerar imagem com IA') as string}
                                  className="p-1.5 bg-white/90 rounded text-purple-600 hover:bg-white"
                                >
                                  <Wand2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </>
                          ) : thumbnailItem?.status === 'uploading' ? (
                            <div className="w-full h-full flex flex-col items-center justify-center">
                              <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />
                              <span className="text-[10px] text-gray-500 mt-1">{thumbnailItem.progress ?? 0}%</span>
                            </div>
                          ) : thumbnailItem?.status === 'generating' ? (
                            <div className="w-full h-full flex flex-col items-center justify-center">
                              <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
                              <span className="text-[10px] text-gray-500 mt-1">
                                {t('social.carousel.generating', 'Gerando...')}
                              </span>
                            </div>
                          ) : (
                            <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded">
                              <div className="flex gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => { setSlideUploadTarget('cover'); slideReplaceInputRef.current?.click(); }}
                                  title={t('social.carousel.addImage', 'Enviar imagem') as string}
                                  className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                >
                                  <Upload className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleGenerateSlideImage('cover')}
                                  title={t('social.carousel.generateImage', 'Gerar imagem com IA') as string}
                                  className="p-1.5 rounded-md bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                                >
                                  <Wand2 className="w-4 h-4" />
                                </button>
                              </div>
                              <span className="text-[10px] text-gray-400 px-1 text-center">
                                {t('social.carousel.noImageYet', 'Sem imagem')}
                              </span>
                            </div>
                          )}
                          <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-bold bg-purple-600/90 text-white rounded">
                            1 · {t('social.carousel.coverLabel', 'Capa')}
                          </span>
                        </div>
                        <textarea
                          value={coverSlideText}
                          onChange={(e) => setCoverSlideText(e.target.value)}
                          placeholder={t('social.carousel.slideTextPlaceholder', 'Texto deste slide (opcional)') as string}
                          rows={2}
                          className="w-full text-xs p-2 bg-transparent border-t border-gray-200 dark:border-gray-700 focus:outline-none resize-none text-gray-800 dark:text-gray-200 placeholder-gray-400"
                        />
                      </div>

                      {/* Slides 2..N */}
                      {extraCarouselItems.map((item, idx) => (
                        <div
                          key={item.id}
                          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
                        >
                          <div className="relative aspect-square bg-gray-100 dark:bg-gray-900 group">
                            {item.status === 'ready' && item.url ? (
                              <>
                                <img src={item.url} alt={`slide-${idx + 2}`} className="w-full h-full object-cover" />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 flex items-center justify-center gap-1 flex-wrap p-1">
                                  <button
                                    type="button"
                                    onClick={() => moveCarouselSlide(item.id, -1)}
                                    disabled={idx === 0}
                                    title={t('social.carousel.moveUp', 'Mover para frente') as string}
                                    className="p-1 bg-white/90 rounded text-gray-700 hover:bg-white disabled:opacity-40"
                                  >
                                    <ArrowUp className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveCarouselSlide(item.id, 1)}
                                    disabled={idx === extraCarouselItems.length - 1}
                                    title={t('social.carousel.moveDown', 'Mover para trás') as string}
                                    className="p-1 bg-white/90 rounded text-gray-700 hover:bg-white disabled:opacity-40"
                                  >
                                    <ArrowDown className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setSlideUploadTarget(item.id); slideReplaceInputRef.current?.click(); }}
                                    title={t('social.carousel.replaceImage', 'Trocar imagem') as string}
                                    className="p-1 bg-white/90 rounded text-gray-700 hover:bg-white"
                                  >
                                    <Upload className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleGenerateSlideImage(item.id)}
                                    title={t('social.carousel.generateImage', 'Gerar imagem com IA') as string}
                                    className="p-1 bg-white/90 rounded text-purple-600 hover:bg-white"
                                  >
                                    <Wand2 className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => promoteSlideToThumbnail(item.id)}
                                    title={t('social.carousel.makeCover', 'Usar como capa') as string}
                                    className="p-1 bg-white/90 rounded text-gray-700 hover:bg-white"
                                  >
                                    <ImageIcon className="w-3 h-3" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeCarouselSlide(item.id)}
                                    title={t('common.remove', 'Remove') as string}
                                    className="p-1 bg-red-500/90 rounded text-white hover:bg-red-600"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </>
                            ) : item.status === 'uploading' ? (
                              <div className="w-full h-full flex flex-col items-center justify-center">
                                <Loader2 className="h-5 w-5 text-primary-500 animate-spin" />
                                <span className="text-[10px] text-gray-500 mt-1">{item.progress ?? 0}%</span>
                              </div>
                            ) : item.status === 'generating' ? (
                              <div className="w-full h-full flex flex-col items-center justify-center">
                                <Loader2 className="h-5 w-5 text-purple-500 animate-spin" />
                                <span className="text-[10px] text-gray-500 mt-1">
                                  {t('social.carousel.generating', 'Gerando...')}
                                </span>
                              </div>
                            ) : (
                              /* Slide sem imagem — o usuário atribui via upload ou IA */
                              <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded">
                                <div className="flex gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => { setSlideUploadTarget(item.id); slideReplaceInputRef.current?.click(); }}
                                    title={t('social.carousel.addImage', 'Enviar imagem') as string}
                                    className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  >
                                    <Upload className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleGenerateSlideImage(item.id)}
                                    title={t('social.carousel.generateImage', 'Gerar imagem com IA') as string}
                                    className="p-1.5 rounded-md bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600"
                                  >
                                    <Wand2 className="w-4 h-4" />
                                  </button>
                                </div>
                                <span className="text-[10px] text-gray-400 px-1 text-center">
                                  {t('social.carousel.noImageYet', 'Sem imagem')}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeCarouselSlide(item.id)}
                                  className="absolute top-1 right-1 p-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-500 hover:text-red-500"
                                  title={t('common.remove', 'Remove') as string}
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                            <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[10px] font-bold bg-black/60 text-white rounded">
                              {idx + 2}
                            </span>
                          </div>
                          <textarea
                            value={item.slideText || ''}
                            onChange={(e) => updateSlideText(item.id, e.target.value)}
                            placeholder={t('social.carousel.slideTextPlaceholder', 'Texto deste slide (opcional)') as string}
                            rows={2}
                            className="w-full text-xs p-2 bg-transparent border-t border-gray-200 dark:border-gray-700 focus:outline-none resize-none text-gray-800 dark:text-gray-200 placeholder-gray-400"
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => carouselFileInputRef.current?.click()}
                      disabled={carouselSlotsLeft === 0}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {t('social.carousel.addSlide', 'Adicionar imagens')}
                    </button>
                    <button
                      type="button"
                      onClick={addEmptySlide}
                      disabled={carouselSlotsLeft === 0}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t('social.carousel.blankSlide', 'Slide em branco')}
                    </button>
                    <input
                      ref={carouselFileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleCarouselFileUpload}
                      className="hidden"
                    />
                    <input
                      ref={slideReplaceInputRef}
                      type="file"
                      accept="image/*"
                      data-testid="carousel-slide-image-input"
                      onChange={handleSlideFileSelected}
                      className="hidden"
                    />

                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-purple-300 dark:border-purple-700 bg-white dark:bg-gray-800">
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {t('social.carousel.aiCount', 'Slides com IA:')}
                      </span>
                      <select
                        value={carouselAiCount}
                        onChange={(e) => setCarouselAiCount(Number(e.target.value))}
                        disabled={isGeneratingCarouselAi}
                        className="text-sm bg-transparent focus:outline-none text-gray-900 dark:text-white"
                      >
                        {[2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleCarouselGenerateAi}
                        disabled={isGeneratingCarouselAi}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGeneratingCarouselAi ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Wand2 className="w-3.5 h-3.5" />
                        )}
                        {t('social.carousel.generateAi', 'Gerar com IA')}
                      </button>
                    </div>
                  </div>

                  {!aiPrompt.trim() && (
                    <p className="text-xs text-gray-400 mt-2">
                      {t('social.carousel.aiHint', 'Dica: escreva o texto em cada slide (ou um prompt no Estúdio de IA) e gere a imagem daquele slide com a varinha.')}
                    </p>
                  )}

                  {/* Várias redes selecionadas: mostra como o carrossel sai em cada uma */}
                  {currentCarouselTotal > 1 && selectedPlatforms.length > 1 && (
                    <div className="mt-3 p-3 rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
                      <div className="flex items-center gap-2 mb-1.5">
                        <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-300 shrink-0" />
                        <p className="text-xs font-medium text-blue-900 dark:text-blue-100">
                          {t('social.carousel.multi.title', 'Você selecionou várias redes — cada uma publica as imagens do seu jeito:')}
                        </p>
                      </div>
                      <ul className="space-y-0.5 pl-6">
                        {selectedPlatforms.map(p => (
                          <li key={p} className="text-xs text-blue-800 dark:text-blue-200">
                            <span className="font-medium">{platformConfig[p]?.name || p}:</span>{' '}
                            {multiNetworkBehavior(p, currentCarouselTotal)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Content Editor */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('social.content', 'Content')}
                  </label>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); startMention(); }}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                    title={t('social.mentionUser', 'Mencionar um usuário na legenda')}
                  >
                    <AtSign className="h-3.5 w-3.5" />
                    {t('social.mention', 'Mencionar')}
                  </button>
                </div>
                <div className="relative">
                  <textarea
                    ref={contentRef}
                    value={content}
                    onChange={handleContentChange}
                    onKeyUp={(e) => updateMentionState(e.currentTarget)}
                    onClick={(e) => updateMentionState(e.currentTarget)}
                    onBlur={() => setTimeout(() => setMentionQuery(null), 150)}
                    placeholder={t('social.contentPlaceholder', 'Type your post here or use AI to generate automatically...')}
                    rows={5}
                    className={`
                      w-full px-4 py-3 rounded-xl border transition-colors resize-none
                      ${isOverLimit
                        ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                        : 'border-gray-200 dark:border-gray-700 focus:ring-primary-500'
                      }
                      bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                      placeholder-gray-400 dark:placeholder-gray-500
                      focus:outline-none focus:ring-2
                    `}
                  />
                  {/* Autocomplete de menções (@) */}
                  {mentionQuery !== null && mentionMatches.length > 0 && (
                    <div className="absolute left-3 right-3 top-full mt-1 z-30 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                      <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700">
                        {t('social.mentionSuggestions', 'Mencionar')}
                      </div>
                      {mentionMatches.map((h) => (
                        <button
                          key={h}
                          type="button"
                          onMouseDown={(e) => { e.preventDefault(); insertMention(h); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-primary-50 dark:hover:bg-primary-900/20 flex items-center gap-2"
                        >
                          <AtSign className="h-3.5 w-3.5 text-gray-400" />@{h}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    <span className={`text-sm ${isOverLimit ? 'text-red-500' : 'text-gray-400'}`}>
                      {content.length}
                      {selectedHashtags.length > 0 && content.length > 0 && (
                        <span className="opacity-60"> +{2 + selectedHashtags.join(' ').length} tags</span>
                      )}
                      /{minMaxLength}
                    </span>
                    {isOverLimit && (
                      <AlertCircle className="h-4 w-4 text-red-500" />
                    )}
                  </div>
                </div>
              </div>

              {/* Menções (separadas do texto — a IA não as apaga; entram na legenda ao publicar) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <AtSign className="h-4 w-4 inline mr-1" />
                    {t('social.mentions', 'Menções')}
                  </label>
                </div>
                {/* Menções selecionadas (chips removíveis) */}
                {selectedMentions.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedMentions.map((h) => (
                      <span
                        key={h}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-lg"
                      >
                        @{h}
                        <button onClick={() => removeMentionHandle(h)} className="hover:text-blue-900 dark:hover:text-blue-100">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {/* Sugestões rápidas: contas conectadas + marcas do Listening */}
                {mentionPool.filter(h => !selectedMentions.includes(h)).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {mentionPool.filter(h => !selectedMentions.includes(h)).slice(0, 12).map((h) => (
                      <button
                        key={h}
                        type="button"
                        onClick={() => addMentionHandle(h)}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-sm rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                      >
                        <AtSign className="h-3 w-3" />{h}
                      </button>
                    ))}
                  </div>
                )}
                {/* Adicionar menção livre */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={mentionInput}
                    onChange={(e) => setMentionInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addMentionHandle(mentionInput); } }}
                    placeholder={t('social.addMention', '@usuario para mencionar...')}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Button variant="secondary" size="sm" onClick={() => addMentionHandle(mentionInput)}>
                    <AtSign className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-gray-400 mt-1">
                  {t('social.mentionsComposerHint', 'Ficam separadas do texto: a IA não apaga e elas entram na legenda ao publicar.')}
                </p>
              </div>

              {/* Hashtags */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <Hash className="h-4 w-4 inline mr-1" />
                    Hashtags
                  </label>
                </div>

                {/* Selected Hashtags */}
                {selectedHashtags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedHashtags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-lg"
                      >
                        {tag}
                        <button onClick={() => toggleHashtag(tag)} className="hover:text-primary-900">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Add Custom Hashtag */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomHashtag())}
                    placeholder={t('social.addHashtag', 'Add hashtag...')}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <Button variant="secondary" size="sm" onClick={addCustomHashtag}>
                    <Hash className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Platform-specific fields */}
              {/* YouTube Settings */}
              {(selectedPlatforms.includes('youtube') || selectedPlatforms.includes('youtube_shorts')) && (
                <div className="space-y-3 p-4 bg-red-50 dark:bg-red-900/10 rounded-xl border border-red-200 dark:border-red-800">
                  <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium text-sm">
                    {PlatformIcons.youtube && <PlatformIcons.youtube className="w-4 h-4" />}
                    <span>{t('social.youtubeSettings', 'YouTube Settings')}</span>
                  </div>
                  <input
                    type="text"
                    placeholder={t('social.videoTitle', 'Video title')}
                    value={youtubeTitle}
                    onChange={(e) => setYoutubeTitle(e.target.value)}
                    maxLength={100}
                    className="w-full px-3 py-2 rounded-lg border border-red-200 dark:border-red-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <input
                    type="text"
                    placeholder={t('social.videoTags', 'Tags (comma separated)')}
                    value={youtubeTags}
                    onChange={(e) => setYoutubeTags(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-red-200 dark:border-red-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <div className="flex gap-2">
                    <select
                      value={youtubeCategory}
                      onChange={(e) => setYoutubeCategory(e.target.value)}
                      className="flex-1 px-3 py-2 rounded-lg border border-red-200 dark:border-red-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="Entertainment">{t('social.categories.entertainment', 'Entertainment')}</option>
                      <option value="Education">{t('social.categories.education', 'Education')}</option>
                      <option value="HowtoStyle">{t('social.categories.howto', 'How-to & Style')}</option>
                      <option value="ScienceTechnology">{t('social.categories.science', 'Science & Technology')}</option>
                      <option value="PeopleBlogs">{t('social.categories.people', 'People & Blogs')}</option>
                    </select>
                    <select
                      value={youtubeVisibility}
                      onChange={(e) => setYoutubeVisibility(e.target.value as 'public' | 'unlisted' | 'private')}
                      className="flex-1 px-3 py-2 rounded-lg border border-red-200 dark:border-red-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <option value="public">{t('social.visibility.public', 'Public')}</option>
                      <option value="unlisted">{t('social.visibility.unlisted', 'Unlisted')}</option>
                      <option value="private">{t('social.visibility.private', 'Private')}</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Instagram Settings */}
              {(selectedPlatforms.includes('instagram') || selectedPlatforms.includes('instagram_reels')) && (
                <div className="space-y-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/10 dark:to-pink-900/10 rounded-xl border border-purple-200 dark:border-purple-800">
                  <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 font-medium text-sm">
                    {PlatformIcons.instagram && <PlatformIcons.instagram className="w-4 h-4" />}
                    <span>
                      {selectedPlatforms.includes('instagram_reels')
                        ? t('social.instagramReelsSettings', 'Instagram Reels Settings')
                        : t('social.instagramSettings', 'Instagram Settings')}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder={t('social.altText', 'Alt text for accessibility')}
                    value={instagramAltText}
                    onChange={(e) => setInstagramAltText(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <textarea
                    placeholder={t('social.firstComment', 'First comment (extra hashtags)')}
                    value={instagramFirstComment}
                    onChange={(e) => setInstagramFirstComment(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  />
                  {selectedPlatforms.includes('instagram_reels') && (
                    <p className="text-xs text-purple-500">
                      {t('social.reelsHint', 'Reels must be vertical (9:16) and up to 15 minutes long')}
                    </p>
                  )}

                  {/* Marcar contas NA FOTO (user_tags) — só posts de imagem do feed */}
                  {selectedPlatforms.includes('instagram') && (
                    <div className="pt-1">
                      <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                        <AtSign className="h-3.5 w-3.5 inline mr-1" />
                        {t('social.igTagPeople', 'Marcar contas na foto')}
                      </label>
                      {igUserTags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {igUserTags.map((h) => (
                            <span key={h} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm rounded-lg">
                              @{h}
                              <button onClick={() => setIgUserTags(prev => prev.filter(x => x !== h))} className="hover:text-purple-900 dark:hover:text-purple-100">
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {mentionPool.filter(h => !igUserTags.includes(h)).length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {mentionPool.filter(h => !igUserTags.includes(h)).slice(0, 10).map((h) => (
                            <button key={h} type="button" onClick={() => addIgUserTag(h)}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-sm rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors">
                              <AtSign className="h-3 w-3" />{h}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={igUserTagInput}
                          onChange={(e) => setIgUserTagInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIgUserTag(igUserTagInput); } }}
                          placeholder={t('social.addMention', '@usuario para marcar...')}
                          className="flex-1 px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <Button variant="secondary" size="sm" onClick={() => addIgUserTag(igUserTagInput)}>
                          <AtSign className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-[11px] text-purple-500/80 mt-1">
                        {t('social.igTagPeopleHint', 'Marca as contas na própria foto (feed), separado da legenda. Só vale para posts de imagem.')}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* LinkedIn Settings */}
              {selectedPlatforms.includes('linkedin') && (
                <div className="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium text-sm">
                      {PlatformIcons.linkedin && <PlatformIcons.linkedin className="w-4 h-4" />}
                      <span>{t('social.linkedinSettings', 'LinkedIn Settings')}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('social.articleMode', 'Article Mode')}</span>
                    <button
                      onClick={() => setLinkedinArticleMode(!linkedinArticleMode)}
                      className={`w-10 h-5 rounded-full transition-colors ${linkedinArticleMode ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                    >
                      <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${linkedinArticleMode ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              )}

              {/* Twitter Settings */}
              {selectedPlatforms.includes('twitter') && (
                <div className="space-y-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-900 dark:text-white font-medium text-sm">
                      {PlatformIcons.twitter && <PlatformIcons.twitter className="w-4 h-4" />}
                      <span>{t('social.twitterSettings', 'X/Twitter Settings')}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('social.threadMode', 'Thread Mode')}</span>
                    <button
                      onClick={() => setTwitterThreadMode(!twitterThreadMode)}
                      className={`w-10 h-5 rounded-full transition-colors ${twitterThreadMode ? 'bg-gray-900 dark:bg-white' : 'bg-gray-300 dark:bg-gray-600'}`}
                    >
                      <div className={`w-4 h-4 rounded-full ${twitterThreadMode ? 'bg-white dark:bg-gray-900' : 'bg-white'} shadow-sm transform transition-transform ${twitterThreadMode ? 'translate-x-5' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              )}

              {/* TikTok Settings — audit-compliant composer.
                  Required by the TikTok Content Sharing Developer Guidelines:
                  https://developers.tiktok.com/doc/content-sharing-guidelines/ */}
              {selectedPlatforms.includes('tiktok') && (
                <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-900 dark:text-white font-medium text-sm">
                      {PlatformIcons.tiktok && <PlatformIcons.tiktok className="w-4 h-4" />}
                      <span>{t('social.tiktokSettings', 'TikTok Settings')}</span>
                    </div>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {t('social.tiktokWillBePosted', 'Your video will be posted to TikTok')}
                    </span>
                  </div>

                  {tiktokCreatorInfoLoading && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      {t('social.tiktokLoadingCreator', 'Loading TikTok account info…')}
                    </div>
                  )}

                  {tiktokCreatorInfoError && (
                    <div className="flex items-start gap-2 p-2 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{tiktokCreatorInfoError}</span>
                    </div>
                  )}

                  {tiktokCreatorInfo && (
                    <>
                      {/* STEP 1 — Creator preview (TikTok Point 1: nickname/avatar visible) */}
                      <div className="flex items-center gap-3 p-2 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700">
                        {tiktokCreatorInfo.creatorAvatarUrl ? (
                          <img
                            src={tiktokCreatorInfo.creatorAvatarUrl}
                            alt={tiktokCreatorInfo.creatorNickname || 'TikTok'}
                            className="w-9 h-9 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-900 to-pink-500" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {tiktokCreatorInfo.creatorNickname || tiktokCreatorInfo.creatorUsername || 'TikTok'}
                          </div>
                          {tiktokCreatorInfo.creatorUsername && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              @{tiktokCreatorInfo.creatorUsername}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] uppercase tracking-wide text-gray-400">
                          {t('social.tiktokPostingAs', 'Posting as')}
                        </span>
                      </div>

                      {/* STEP 2 — Caption preview (TikTok Point 5: show what will be posted).
                          Mirrors EXACTLY what the submit handler builds: caption + hashtags
                          appended with two newlines. Length is counted against TikTok's 2200
                          char limit so the reviewer (and the user) see truncation risk early. */}
                      {(() => {
                        const previewText = buildFinalContent(content);
                        const tikTokLimit = 2200;
                        const over = previewText.length > tikTokLimit;
                        return (
                          <div className="rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-[10px] uppercase tracking-wide text-gray-400">
                                {t('social.tiktokCaption', 'Caption')}
                              </span>
                              <span className={`text-[10px] ${over ? 'text-red-500' : 'text-gray-400'}`}>
                                {previewText.length}/{tikTokLimit}
                              </span>
                            </div>
                            <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words min-h-[1.25rem]">
                              {previewText.trim()
                                ? previewText
                                : <span className="text-gray-400 italic">{t('social.tiktokCaptionEmpty', 'Type a caption above…')}</span>}
                            </div>
                            {over && (
                              <div className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                                {t('social.tiktokCaptionOver', 'TikTok will truncate the caption at 2200 characters.')}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* STEP 3 — Privacy selector. No default value (Point 2). */}
                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                          {t('social.tiktokPrivacy', 'Who can view this video')}
                          <span className="text-red-500 ml-0.5">*</span>
                        </label>
                        <select
                          value={tiktokPrivacyLevel}
                          onChange={(e) => setTiktokPrivacyLevel(e.target.value as any)}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-pink-500"
                        >
                          <option value="" disabled>
                            {t('social.tiktokPrivacyPlaceholder', 'Choose audience…')}
                          </option>
                          {tiktokCreatorInfo.privacyLevelOptions.map(opt => {
                            const labels: Record<string, string> = {
                              PUBLIC_TO_EVERYONE: t('social.tiktokPrivacyPublic', 'Public'),
                              MUTUAL_FOLLOW_FRIENDS: t('social.tiktokPrivacyFriends', 'Friends'),
                              FOLLOWER_OF_CREATOR: t('social.tiktokPrivacyFollowers', 'Followers'),
                              SELF_ONLY: t('social.tiktokPrivacyOnlyMe', 'Only Me')
                            };
                            const isDisabled = tiktokDiscloseEnabled && tiktokBrandContent && opt === 'SELF_ONLY';
                            return (
                              <option key={opt} value={opt} disabled={isDisabled}>
                                {labels[opt] || opt}{isDisabled ? ' — ' + t('social.tiktokNotAllowedWithBranded', 'not allowed with Branded Content') : ''}
                              </option>
                            );
                          })}
                        </select>
                      </div>

                      {/* STEP 4 — Interaction toggles (Point 2: not pre-checked; account-disabled
                          interactions render greyed out). */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {t('social.tiktokInteractions', 'Allow users to')}
                        </div>
                        {[
                          { label: t('social.allowComment', 'Comment'), value: tiktokCommentEnabled, onChange: setTiktokCommentEnabled, disabled: tiktokCreatorInfo.commentDisabled, key: 'comment' },
                          { label: t('social.allowDuet', 'Duet'), value: tiktokDuetEnabled, onChange: setTiktokDuetEnabled, disabled: tiktokCreatorInfo.duetDisabled, key: 'duet' },
                          { label: t('social.allowStitch', 'Stitch'), value: tiktokStitchEnabled, onChange: setTiktokStitchEnabled, disabled: tiktokCreatorInfo.stitchDisabled, key: 'stitch' }
                        ].map(item => (
                          <div key={item.key} className="flex items-center justify-between">
                            <span className={`text-sm ${item.disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                              {item.label}
                              {item.disabled && (
                                <span className="ml-2 text-[10px] text-gray-400">
                                  {t('social.tiktokDisabledByAccount', 'disabled by this account')}
                                </span>
                              )}
                            </span>
                            <button
                              type="button"
                              disabled={item.disabled}
                              onClick={() => !item.disabled && item.onChange(!item.value)}
                              className={`w-10 h-5 rounded-full transition-colors ${item.disabled ? 'opacity-40 cursor-not-allowed bg-gray-200 dark:bg-gray-700' : (item.value && !item.disabled ? 'bg-pink-500' : 'bg-gray-300 dark:bg-gray-600')}`}
                            >
                              <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${(item.value && !item.disabled) ? 'translate-x-5' : 'translate-x-0.5'}`} />
                            </button>
                          </div>
                        ))}
                      </div>

                      {/* STEP 5 — Disclose post content (Point 3). Toggle off by default.
                          When ON, exactly one of "Your Brand" / "Branded Content" must be checked.
                          The visible label ("Promotional content" / "Paid partnership") appears
                          right under the selected option as TikTok's UX requires. */}
                      <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {t('social.tiktokDisclose', 'Disclose video content')}
                            </div>
                            <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                              {t('social.tiktokDiscloseHelp', 'Turn on if this video promotes a brand, product or service, either your own or a third party.')}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const next = !tiktokDiscloseEnabled;
                              setTiktokDiscloseEnabled(next);
                              if (!next) {
                                setTiktokBrandOrganic(false);
                                setTiktokBrandContent(false);
                              }
                            }}
                            className={`w-10 h-5 rounded-full transition-colors shrink-0 ${tiktokDiscloseEnabled ? 'bg-pink-500' : 'bg-gray-300 dark:bg-gray-600'}`}
                          >
                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform ${tiktokDiscloseEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>

                        {tiktokDiscloseEnabled && (
                          <div className="space-y-2 pl-1">
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={tiktokBrandOrganic}
                                onChange={(e) => setTiktokBrandOrganic(e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                  {t('social.tiktokYourBrand', 'Your Brand')}
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                  {t('social.tiktokYourBrandHelp', 'You are promoting yourself or your own business.')}
                                </div>
                                {tiktokBrandOrganic && (
                                  <div className="mt-1 text-[11px] font-medium text-pink-600 dark:text-pink-400">
                                    {t('social.tiktokLabelPromotional', 'Your video will be labeled as "Promotional content".')}
                                  </div>
                                )}
                              </div>
                            </label>
                            <label className="flex items-start gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={tiktokBrandContent}
                                onChange={(e) => setTiktokBrandContent(e.target.checked)}
                                className="mt-0.5 w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                              />
                              <div className="flex-1">
                                <div className="text-sm text-gray-700 dark:text-gray-300">
                                  {t('social.tiktokBrandedContent', 'Branded Content')}
                                </div>
                                <div className="text-[11px] text-gray-500 dark:text-gray-400">
                                  {t('social.tiktokBrandedContentHelp', 'You are promoting another brand or third party. Cannot be combined with Only Me privacy.')}
                                </div>
                                {tiktokBrandContent && (
                                  <div className="mt-1 text-[11px] font-medium text-pink-600 dark:text-pink-400">
                                    {t('social.tiktokLabelPaidPartnership', 'Your video will be labeled as "Paid partnership".')}
                                  </div>
                                )}
                              </div>
                            </label>
                            {tiktokDiscloseEnabled && !tiktokBrandOrganic && !tiktokBrandContent && (
                              <div className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400 mt-1">
                                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                <span>{t('social.tiktokDisclosePickOne', 'Pick at least one option to continue.')}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* STEP 6 — Music Usage Confirmation (Point 2 requires a music declaration).
                          Required acknowledgment, must be explicitly checked before publishing. */}
                      <div className="space-y-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={tiktokMusicConfirmed}
                            onChange={(e) => setTiktokMusicConfirmed(e.target.checked)}
                            className="mt-0.5 w-4 h-4 rounded text-pink-500 focus:ring-pink-500"
                          />
                          <div className="flex-1 text-[12px] text-gray-700 dark:text-gray-300 leading-relaxed">
                            {tiktokDiscloseEnabled && tiktokBrandContent ? (
                              <>
                                {t('social.tiktokDisclaimerBranded', 'By posting, I agree to TikTok\'s')}{' '}
                                <a href="https://www.tiktok.com/legal/page/global/bc-policy/en" target="_blank" rel="noopener noreferrer" className="underline hover:text-pink-500" onClick={e => e.stopPropagation()}>
                                  {t('social.tiktokBrandedContentPolicy', 'Branded Content Policy')}
                                </a>{' '}
                                {t('social.and', 'and')}{' '}
                                <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" className="underline hover:text-pink-500" onClick={e => e.stopPropagation()}>
                                  {t('social.tiktokMusicUsage', 'Music Usage Confirmation')}
                                </a>.
                              </>
                            ) : (
                              <>
                                {t('social.tiktokDisclaimer', 'By posting, I agree to TikTok\'s')}{' '}
                                <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank" rel="noopener noreferrer" className="underline hover:text-pink-500" onClick={e => e.stopPropagation()}>
                                  {t('social.tiktokMusicUsage', 'Music Usage Confirmation')}
                                </a>.
                              </>
                            )}
                          </div>
                        </label>
                      </div>

                      {/* Processing notice (Point 5: notify users that processing may take time) */}
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 italic">
                        {t('social.tiktokProcessingNotice', 'TikTok may take a few minutes to process and publish the video after upload.')}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Video Upload — rendered BEFORE the image/thumbnail so users upload video first,
                  which also unlocks the "pick frame from video" shortcut on the image picker below. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Video className="h-4 w-4 inline mr-1" />
                  {t('social.video', 'Video')}
                  {requiresVideo ? (
                    <span className="text-red-500 ml-1">*</span>
                  ) : (
                    <span className="text-xs text-gray-400 ml-1">({t('common.optional', 'optional')})</span>
                  )}
                </label>
                <div className="flex flex-wrap gap-3">
                  {videoItem ? (
                    <div className="relative w-32 h-24 sm:w-40 sm:h-28 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      {videoItem.status === 'generating' ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                          <Loader2 className="h-6 w-6 text-purple-500 animate-spin mb-1" />
                          <span className="text-xs text-purple-600 dark:text-purple-400">{t('social.generating', 'Generating...')}</span>
                        </div>
                      ) : videoItem.status === 'uploading' ? (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                          <Loader2 className="h-6 w-6 text-primary-500 animate-spin mb-1" />
                          <span className="text-xs text-gray-500">{videoItem.progress}%</span>
                        </div>
                      ) : (
                        <div
                          className="relative w-full h-full cursor-pointer group"
                          onClick={() => setShowVideoPreview(true)}
                        >
                          <video src={videoItem.url} className="w-full h-full object-cover object-center" muted />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                            <Play className="h-8 w-8 text-white group-hover:scale-110 transition-transform" />
                          </div>
                          {videoItem.duration && (
                            <span className="absolute bottom-1 right-1 text-xs bg-black/70 text-white px-1 rounded">
                              {Math.round(videoItem.duration)}s
                            </span>
                          )}
                        </div>
                      )}
                      {videoItem.status === 'ready' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); removeVideo(); }}
                          className="absolute top-1 right-1 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 z-10"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => videoInputRef.current?.click()}
                      className={`w-32 h-24 sm:w-40 sm:h-28 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${
                        requiresVideo
                          ? 'border-red-300 dark:border-red-700 text-red-400 hover:text-red-500 hover:border-red-400'
                          : 'border-gray-300 dark:border-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <Upload className="h-6 w-6" />
                      <span className="text-xs">{t('social.uploadVideo', 'Upload Video')}</span>
                      <span className="text-xs">{t('social.maxSize', 'Up to 2GB')}</span>
                    </button>
                  )}
                </div>
                {/* Video library shortcut — pick from Video Studio assets the user already has */}
                {!videoItem && (
                  <button
                    onClick={() => setShowLibrary('video')}
                    className="mt-2 h-9 px-3 rounded-lg border border-cyan-300 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300 text-xs font-medium hover:bg-cyan-50 dark:hover:bg-cyan-900/20 inline-flex items-center gap-1.5 transition-colors"
                  >
                    <Film className="h-3.5 w-3.5" />
                    {t('social.useFromVideoLibrary', 'Usar da biblioteca de vídeos')}
                  </button>
                )}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/mp4,video/quicktime,.mp4,.mov"
                  onChange={handleVideoUpload}
                  className="hidden"
                />
                {requiresVideo && !videoItem && (
                  <p className="text-xs text-red-500 mt-1">
                    {t('social.videoRequiredHint', 'Video required for: ')}
                    {selectedPlatforms.filter(p => platformConfig[p]?.requiresVideo).map(p => platformConfig[p]?.name).join(', ')}
                  </p>
                )}
              </div>

              {/* Image Upload - shown after video so the frame-picker option flows naturally */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <ImageIcon className="h-4 w-4 inline mr-1" />
                  {requiresVideo
                    ? t('social.thumbnail', 'Thumbnail')
                    : t('social.image', 'Image')}
                  {requiresThumbnail && <span className="text-red-500 ml-1">*</span>}
                </label>
                <div className="flex flex-wrap gap-3">
                  {thumbnailItem ? (
                    <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700">
                      {thumbnailItem.status === 'generating' ? (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                          <Loader2 className="h-6 w-6 text-purple-500 animate-spin mb-1" />
                          <span className="text-xs text-purple-600 dark:text-purple-400">{t('social.generating', 'Generating...')}</span>
                        </div>
                      ) : thumbnailItem.status === 'uploading' ? (
                        <div className="w-full h-full flex flex-col items-center justify-center">
                          <Loader2 className="h-6 w-6 text-primary-500 animate-spin mb-1" />
                          <span className="text-xs text-gray-500">{thumbnailItem.progress}%</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => thumbnailItem.status === 'ready' && setShowImagePreview(true)}
                          title={t('social.imageActions.viewFull', 'Ver imagem inteira') as string}
                          aria-label={t('social.imageActions.viewFull', 'Ver imagem inteira') as string}
                          className="block w-full h-full cursor-zoom-in"
                        >
                          <img src={thumbnailItem.url} alt="Image" className="w-full h-full object-cover object-center" />
                        </button>
                      )}
                      {thumbnailItem.status === 'ready' && (
                        <>
                          <div className="absolute top-1 right-1 flex gap-1">
                            <button
                              type="button"
                              onClick={() => setShowImageEditor(true)}
                              title={t('social.imageActions.edit', 'Editar imagem') as string}
                              aria-label={t('social.imageActions.edit', 'Editar imagem') as string}
                              className="p-1.5 bg-purple-600 rounded-full text-white shadow-md ring-1 ring-white/70 hover:bg-purple-700"
                            >
                              <Palette className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={removeThumbnail}
                              title={t('social.imageActions.remove', 'Remover imagem') as string}
                              aria-label={t('social.imageActions.remove', 'Remover imagem') as string}
                              className="p-1.5 bg-black/70 rounded-full text-white shadow-md ring-1 ring-white/70 hover:bg-red-600"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          {/* Indica que a miniatura abre a imagem inteira */}
                          <span className="pointer-events-none absolute bottom-1 left-1 p-1 bg-black/70 rounded-md text-white shadow-md">
                            <Maximize2 className="h-3.5 w-3.5" />
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className={`w-28 h-28 sm:w-32 sm:h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${
                        requiresThumbnail
                          ? 'border-red-300 dark:border-red-700 text-red-400 hover:text-red-500 hover:border-red-400'
                          : 'border-gray-300 dark:border-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <ImagePlus className="h-6 w-6" />
                      <span className="text-xs">{requiresVideo ? t('social.uploadThumbnail', 'Upload Image') : t('social.uploadImage', 'Upload Image')}</span>
                    </button>
                  )}
                </div>
                {/* Action buttons row (Edit/View image, Image Studio + Pick frame from video) */}
                <div className="flex flex-wrap gap-2 mt-2">
                  {thumbnailItem?.status === 'ready' && (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowImageEditor(true)}
                        className="h-10 px-3 rounded-lg bg-purple-600 text-white text-xs font-semibold shadow-sm hover:bg-purple-700 flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <Palette className="h-4 w-4" />
                        {t('social.imageActions.edit', 'Editar imagem')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowImagePreview(true)}
                        className="h-10 px-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <Maximize2 className="h-4 w-4" />
                        {t('social.imageActions.viewFull', 'Ver imagem inteira')}
                      </button>
                    </>
                  )}
                  {!thumbnailItem && (
                    <button
                      onClick={() => setShowImageStudio(true)}
                      className="h-10 px-3 rounded-lg border border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 text-xs font-medium hover:bg-purple-50 dark:hover:bg-purple-900/20 flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <Palette className="h-3.5 w-3.5" />
                      Image Studio
                    </button>
                  )}
                  {/* Pick thumbnail from video frames — shown only when a video is present */}
                  {videoItem && videoItem.status === 'ready' && (
                    <button
                      onClick={() => setShowFramePicker(true)}
                      className="h-10 px-3 rounded-lg border border-indigo-300 dark:border-indigo-700 text-indigo-600 dark:text-indigo-400 text-xs font-medium hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex items-center justify-center gap-1.5 transition-colors"
                    >
                      <Film className="h-3.5 w-3.5" />
                      {t('social.pickFrameFromVideo', 'Escolher frame do vídeo')}
                    </button>
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleThumbnailUpload}
                  className="hidden"
                />
                {requiresThumbnail && !thumbnailItem && (
                  <p className="text-xs text-red-500 mt-1">{t('social.thumbnailRequiredHint', 'Thumbnail required for YouTube')}</p>
                )}
                {!requiresVideo && !thumbnailItem && !videoItem && (
                  <p className="text-xs text-gray-400 mt-1">
                    Publish with image only on: {selectedPlatforms.filter(p => platformConfig[p]?.supportsImage).map(p => platformConfig[p]?.name).join(', ')}
                  </p>
                )}
              </div>

              {/* Trial Reel (Instagram Reels only) */}
              {selectedPlatforms.includes('instagram_reels') && (
                <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10 p-4">
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={trialReelEnabled}
                      onChange={(e) => setTrialReelEnabled(e.target.checked)}
                      className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200 inline-flex items-center gap-1.5">
                        <Film className="w-4 h-4 text-purple-500" />
                        {t('social.trialReel.title', 'Publicar como Trial Reel')}
                      </span>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {t('social.trialReel.hint', 'O reel vai só para não-seguidores primeiro. Se performar bem, você decide se libera para seus seguidores.')}
                      </p>
                    </div>
                  </label>

                  {trialReelEnabled && (
                    <div className="mt-3 pl-7 space-y-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                        {t('social.trialReel.strategy', 'Como publicar para seguidores depois')}
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          type="button"
                          onClick={() => setTrialGraduationStrategy('MANUAL')}
                          className={`flex-1 text-left px-3 py-2 rounded-lg border text-sm transition ${
                            trialGraduationStrategy === 'MANUAL'
                              ? 'border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                              : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-purple-300'
                          }`}
                        >
                          <div className="font-medium">{t('social.trialReel.manual', 'Manual')}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {t('social.trialReel.manualHint', 'Você decide no app do Instagram quando liberar para seguidores.')}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setTrialGraduationStrategy('SS_PERFORMANCE')}
                          className={`flex-1 text-left px-3 py-2 rounded-lg border text-sm transition ${
                            trialGraduationStrategy === 'SS_PERFORMANCE'
                              ? 'border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
                              : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-purple-300'
                          }`}
                        >
                          <div className="font-medium">{t('social.trialReel.automatic', 'Automático')}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {t('social.trialReel.automaticHint', 'O Instagram libera automaticamente se o reel performar bem.')}
                          </div>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Schedule */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  {t('social.scheduleAt', 'Schedule for')}
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    min={formatDateForInput(new Date().toISOString())}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="flex-1 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <Clock className="h-4 w-4" />
                    <span>{t('social.bestTimeTip', 'Dica: 18h–21h costuma engajar mais')}</span>
                  </div>
                </div>
                {/* Live confirmation of the exact moment + timezone the post will
                    go out — so the user can't accidentally schedule the wrong
                    day/time or be caught out by a different browser timezone. */}
                {(() => {
                  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                  const utc = localDatetimeToUTC(scheduledAt);
                  const d = utc ? new Date(utc) : null;
                  if (d && !isNaN(d.getTime())) {
                    // Don't promise "will publish" for a past / too-soon time — it
                    // would contradict the validator that blocks it on submit.
                    if (d.getTime() < Date.now() + 2 * 60 * 1000) {
                      return (
                        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {t('social.scheduleTooSoonHint', 'Escolha um horário pelo menos 2 minutos à frente.')}
                        </p>
                      );
                    }
                    const when = d.toLocaleString(i18n.language || undefined, {
                      weekday: 'long', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    });
                    return (
                      <p className="mt-2 text-xs text-primary-600 dark:text-primary-400 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {t('social.willPublishAt', 'Será publicado em {{when}} ({{tz}})', { when, tz })}
                      </p>
                    );
                  }
                  return (
                    <p className="mt-2 text-xs text-gray-400">
                      {t('social.scheduleTzHint', 'O horário escolhido usa o seu fuso ({{tz}})', { tz })}
                    </p>
                  );
                })()}
              </div>
            </div>

            {/* Preview Section */}
            <div className="w-full lg:w-80 bg-gray-50 dark:bg-gray-900 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 lg:overflow-y-auto">
              {/* Mobile toggle */}
              <button
                onClick={() => setShowMobilePreview(!showMobilePreview)}
                className="lg:hidden w-full flex items-center justify-between p-4 text-gray-700 dark:text-gray-300"
              >
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span className="font-medium">{t('social.preview', 'Preview')}</span>
                </div>
                {showMobilePreview ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
              <div className={`${showMobilePreview ? 'block p-4 pt-0' : 'hidden'} lg:block lg:p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-gray-900 dark:text-white">
                  <span className="hidden lg:inline">{t('social.preview', 'Preview')}</span>
                  {selectedPlatforms.length > 1 && (
                    <span className="lg:hidden text-sm text-gray-500 dark:text-gray-400">
                      {t('social.selectPlatformPreview', 'Platform:')}
                    </span>
                  )}
                </h3>
                {selectedPlatforms.length > 1 && (
                  <select
                    value={previewPlatform}
                    onChange={(e) => setPreviewPlatform(e.target.value)}
                    className="text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {selectedPlatforms.map((p) => (
                      <option key={p} value={p}>
                        {platforms.find(pl => pl.id === p)?.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Platform-specific Preview */}
              {(() => {
                // Get logo position classes
                const logoPositionClasses: Record<string, string> = {
                  'top-left': 'top-2 left-2',
                  'top-right': 'top-2 right-2',
                  'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                  'bottom-left': 'bottom-2 left-2',
                  'bottom-right': 'bottom-2 right-2'
                };

                // Determine aspect ratio based on platform
                const getAspectRatio = (platform: string) => {
                  const verticalPlatforms = ['tiktok', 'youtube_shorts', 'instagram_reels', 'instagram_stories', 'facebook_stories'];
                  const horizontalPlatforms = ['youtube', 'linkedin', 'facebook'];
                  if (verticalPlatforms.includes(platform)) return 'aspect-[9/16]';
                  if (horizontalPlatforms.includes(platform)) return 'aspect-video';
                  return 'aspect-square'; // instagram, twitter, threads
                };

                // Carrossel no preview: capa + extras prontos, navegáveis com ‹ › e dots.
                const carouselPreviewUrls = (PLATAFORMAS_COM_CARROSSEL.includes(previewPlatform || selectedPlatforms[0] || '') && !videoItem)
                  ? [thumbnailItem, ...extraCarouselItems]
                      .filter((it): it is MediaItem => !!it && it.status === 'ready' && !!it.url)
                      .map(it => it.url)
                  : [];
                const showCarouselPreview = carouselPreviewUrls.length > 1;
                const safeSlideIdx = showCarouselPreview ? Math.min(previewSlideIdx, carouselPreviewUrls.length - 1) : 0;

                // Media preview with logo overlay
                const MediaPreview = ({ aspectClass }: { aspectClass: string }) => (
                  <div className={`${aspectClass} bg-gray-900 relative overflow-hidden flex items-center justify-center`}>
                    {showCarouselPreview ? (
                      <>
                        <img
                          src={carouselPreviewUrls[safeSlideIdx]}
                          alt={`slide-${safeSlideIdx + 1}`}
                          className="absolute inset-0 w-full h-full object-cover object-center"
                        />
                        <button
                          type="button"
                          onClick={() => setPreviewSlideIdx((safeSlideIdx - 1 + carouselPreviewUrls.length) % carouselPreviewUrls.length)}
                          className="absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 text-white hover:bg-black/70"
                          aria-label="previous slide"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPreviewSlideIdx((safeSlideIdx + 1) % carouselPreviewUrls.length)}
                          className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 text-white hover:bg-black/70"
                          aria-label="next slide"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                        <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-medium bg-black/60 text-white rounded-full">
                          {safeSlideIdx + 1}/{carouselPreviewUrls.length}
                        </span>
                        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
                          {carouselPreviewUrls.map((_, i) => (
                            <span
                              key={i}
                              className={`w-1.5 h-1.5 rounded-full ${i === safeSlideIdx ? 'bg-white' : 'bg-white/40'}`}
                            />
                          ))}
                        </div>
                      </>
                    ) : videoItem?.status === 'ready' ? (
                      <>
                        {thumbnailItem?.status === 'ready' ? (
                          <img src={thumbnailItem.url} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
                        ) : (
                          <video src={videoItem.url} className="absolute inset-0 w-full h-full object-cover object-center" muted />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="h-10 w-10 text-white" />
                        </div>
                      </>
                    ) : thumbnailItem?.status === 'ready' ? (
                      <img src={thumbnailItem.url} alt="" className="absolute inset-0 w-full h-full object-cover object-center" />
                    ) : (
                      <div className="flex items-center justify-center text-gray-500">
                        <ImageIcon className="h-10 w-10" />
                      </div>
                    )}
                  </div>
                );

                const currentPlatform = previewPlatform || selectedPlatforms[0] || '';
                const PlatformIcon = PlatformIcons[currentPlatform];
                // Resolve the connected account for this platform so the preview shows
                // the real username + avatar instead of "yourbrand". Some platforms share
                // credentials (e.g. instagram_reels uses instagram's account).
                const platformLinkage: Record<string, string> = {
                  instagram_reels: 'instagram',
                  youtube_shorts: 'youtube',
                };
                const accountPlatform = platformLinkage[currentPlatform] || currentPlatform;
                const activeAccount = connectedAccounts.find(a => a.platform === accountPlatform && a.connected)
                  || connectedAccounts.find(a => a.platform === currentPlatform && a.connected);
                const brandName = activeAccount?.username || 'yourbrand';
                const brandAvatar = activeAccount?.profile_image || null;
                const aspectClass = getAspectRatio(currentPlatform);

                // Different preview layouts per platform type
                const isVertical = ['tiktok', 'youtube_shorts', 'instagram_reels', 'instagram_stories', 'facebook_stories'].includes(currentPlatform);
                const isYouTube = ['youtube', 'youtube_shorts'].includes(currentPlatform);
                const isTwitter = currentPlatform === 'twitter';

                return (
                  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {/* Platform indicator */}
                    <div className={`flex items-center gap-2 px-3 py-2 bg-gradient-to-r ${platformConfig[currentPlatform]?.color || 'from-gray-400 to-gray-500'}`}>
                      {PlatformIcon && <PlatformIcon className="h-4 w-4 text-white" />}
                      <span className="text-xs font-medium text-white">{platformConfig[currentPlatform]?.name}</span>
                    </div>

                    {isYouTube ? (
                      // YouTube style preview
                      <>
                        <MediaPreview aspectClass="aspect-video" />
                        <div className="p-3">
                          <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                            {youtubeTitle || content.substring(0, 60) || 'Video title'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{brandName} • 0 views</p>
                        </div>
                      </>
                    ) : isVertical ? (
                      // TikTok/Reels/Shorts style preview (vertical)
                      <div className="relative">
                        <MediaPreview aspectClass="aspect-[9/16] max-h-80" />
                        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                          <p className="text-xs text-white font-medium">@{brandName}</p>
                          <p className="text-xs text-white/90 line-clamp-2 mt-1">
                            {content || 'Video description...'}
                          </p>
                          {selectedHashtags.length > 0 && (
                            <p className="text-xs text-white/70 mt-1">
                              {selectedHashtags.slice(0, 3).join(' ')}
                            </p>
                          )}
                        </div>
                        {/* Vertical icons sidebar (TikTok style) */}
                        <div className="absolute right-2 bottom-20 flex flex-col gap-3">
                          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="text-white text-xs">❤️</span>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="text-white text-xs">💬</span>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                            <span className="text-white text-xs">↗️</span>
                          </div>
                        </div>
                      </div>
                    ) : isTwitter ? (
                      // Twitter/X style preview
                      <>
                        <div className="p-3 border-b border-gray-100 dark:border-gray-700">
                          <div className="flex gap-3">
                            {brandAvatar ? (
                              <img src={brandAvatar} alt={brandName} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-400 to-gray-500 flex-shrink-0" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-1">
                                <span className="text-sm font-bold text-gray-900 dark:text-white">{brandName}</span>
                                <span className="text-sm text-gray-500">@{brandName} · now</span>
                              </div>
                              <p className="text-sm text-gray-900 dark:text-white mt-1 whitespace-pre-wrap">
                                {content || 'What\'s happening?'}
                              </p>
                              {selectedMentions.length > 0 && (
                                <p className="text-sm text-blue-500 mt-1">
                                  {selectedMentions.map(m => '@' + m).join(' ')}
                                </p>
                              )}
                              {selectedHashtags.length > 0 && (
                                <p className="text-sm text-blue-500 mt-1">
                                  {selectedHashtags.join(' ')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                        {(thumbnailItem?.status === 'ready' || videoItem?.status === 'ready') && (
                          <div className="mx-3 mb-3 rounded-xl overflow-hidden">
                            <MediaPreview aspectClass="aspect-video" />
                          </div>
                        )}
                      </>
                    ) : (
                      // Instagram/Facebook/LinkedIn style preview (square/feed)
                      <>
                        <div className="flex items-center gap-3 p-3 border-b border-gray-100 dark:border-gray-700">
                          {brandAvatar ? (
                            <img src={brandAvatar} alt={brandName} className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${platformConfig[currentPlatform]?.color || 'from-gray-400 to-gray-500'}`} />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{brandName}</p>
                          </div>
                        </div>
                        {(thumbnailItem?.status === 'ready' || videoItem?.status === 'ready') && (
                          <MediaPreview aspectClass={aspectClass} />
                        )}
                        <div className="p-3">
                          <p className="text-sm text-gray-900 dark:text-white">
                            <span className="font-medium">{brandName}</span>{' '}
                            <span className="whitespace-pre-wrap line-clamp-3">
                              {content || t('social.captionPlaceholder', 'Your caption here...')}
                            </span>
                          </p>
                          {selectedMentions.length > 0 && (
                            <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                              {selectedMentions.map(m => '@' + m).join(' ')}
                            </p>
                          )}
                          {selectedHashtags.length > 0 && (
                            <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                              {selectedHashtags.join(' ')}
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    {/* Format indicator */}
                    {(videoDimensions || imageDimensions) && (
                      <div className="px-3 pb-2">
                        <span className="text-xs text-gray-400">
                          {videoDimensions
                            ? `📹 ${videoDimensions.width}x${videoDimensions.height} ${videoDimensions.height > videoDimensions.width ? '(Vertical)' : '(Horizontal)'}`
                            : imageDimensions
                            ? `🖼️ ${imageDimensions.width}x${imageDimensions.height} ${imageDimensions.height > imageDimensions.width ? '(Vertical)' : '(Horizontal)'}`
                            : ''}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 sticky bottom-0">
            <button
              onClick={onClose}
              className="hidden sm:block px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={onClose}
                className="sm:hidden flex-1 px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded-lg"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <Button
                variant="secondary"
                onClick={() => handleSubmit('draft')}
                disabled={!canSubmit || isSubmitting}
                title={submitBlockedReason || undefined}
                className="flex-1 sm:flex-none text-sm"
              >
                {t('social.saveAsDraft', 'Draft')}
              </Button>
              {/* Publish Now — always available, ignores any scheduled date.
                  TikTok requires the publish button to be disabled while the composer
                  is in an inconsistent state (no privacy, missing disclosure choice,
                  branded+self_only, or music ack pending) — see tiktokSubmitBlocked. */}
              <Button
                variant="secondary"
                onClick={() => handleSubmit('publish_now')}
                disabled={!canSubmit || isSubmitting || tiktokSubmitBlocked}
                className="gap-2 flex-1 sm:flex-none text-sm"
                title={submitBlockedReason || (tiktokSubmitBlocked ? (t('social.tiktokSubmitBlocked', 'Complete the TikTok options before posting') as string) : (t('social.publishNowTooltip', 'Publicar imediatamente, ignorando a data agendada') as string))}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">
                  {isSubmitting ? t('social.publishing', 'Publishing...') : t('social.publishNow', 'Publish Now')}
                </span>
                <span className="sm:hidden">
                  {isSubmitting ? '...' : t('social.publish', 'Publish')}
                </span>
              </Button>
              {/* Schedule — only enabled when a date is set + TikTok requirements met */}
              <Button
                variant="primary"
                onClick={() => handleSubmit('scheduled')}
                disabled={!canSubmit || isSubmitting || !scheduledAt || tiktokSubmitBlocked}
                className="gap-2 flex-1 sm:flex-none text-sm"
                title={submitBlockedReason || (tiktokSubmitBlocked ? (t('social.tiktokSubmitBlocked', 'Complete the TikTok options before posting') as string) : (!scheduledAt ? (t('social.scheduleRequiresDate', 'Defina uma data acima para agendar') as string) : undefined))}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Calendar className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">
                  {isSubmitting ? t('social.publishing', 'Publishing...') : t('social.schedule', 'Schedule')}
                </span>
                <span className="sm:hidden">
                  {isSubmitting ? '...' : t('social.schedule', 'Schedule')}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Video Preview Modal */}
      {showVideoPreview && videoItem?.url && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80"
          onClick={() => setShowVideoPreview(false)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full mx-4" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setShowVideoPreview(false)}
              className="absolute -top-10 right-0 p-2 text-white hover:text-gray-300 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <video
              src={videoItem.url}
              controls
              autoPlay
              className="w-full max-h-[80vh] rounded-lg"
            />
          </div>
        </div>
      )}
      {/* Image Preview Modal — imagem inteira, sem o corte quadrado da miniatura */}
      {showImagePreview && thumbnailItem?.url && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/85 p-4"
          onClick={() => setShowImagePreview(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="relative flex flex-col items-center w-full max-w-5xl" onClick={e => e.stopPropagation()}>
            <img
              src={thumbnailItem.url}
              alt=""
              className="max-w-full max-h-[78vh] object-contain rounded-lg"
            />
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => { setShowImagePreview(false); setShowImageEditor(true); }}
                className="h-10 px-4 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 inline-flex items-center gap-2 transition-colors"
              >
                <Palette className="h-4 w-4" />
                {t('social.imageActions.edit', 'Editar imagem')}
              </button>
              <button
                type="button"
                onClick={() => setShowImagePreview(false)}
                className="h-10 px-4 rounded-lg bg-white/15 text-white text-sm font-medium hover:bg-white/25 inline-flex items-center gap-2 transition-colors"
              >
                <X className="h-4 w-4" />
                {t('social.imageActions.close', 'Fechar')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Image Studio Modal */}
      <ImageStudioModal
        isOpen={showImageStudio}
        onClose={() => setShowImageStudio(false)}
        onSelect={(imageUrl) => {
          setThumbnailItem({
            id: `studio-${Date.now()}`,
            url: imageUrl,
            type: 'image',
            status: 'ready'
          });
          setShowImageStudio(false);
        }}
        mode="all"
      />

      {/* Image Editor Modal — opens on top without leaving the post */}
      <ImageEditorModal
        isOpen={showImageEditor}
        onClose={() => setShowImageEditor(false)}
        imageUrl={thumbnailItem?.url}
        onSave={(newUrl) => {
          setThumbnailItem({
            id: `edited-${Date.now()}`,
            url: newUrl,
            type: 'image',
            status: 'ready'
          });
          setShowImageEditor(false);
        }}
      />

      {/* Video Frame Picker — extracts 6 frames from the uploaded video */}
      {showFramePicker && videoItem && (
        <VideoFramePicker
          videoFile={videoFile}
          videoUrl={videoItem.url}
          onClose={() => setShowFramePicker(false)}
          onSelect={async (file) => {
            setShowFramePicker(false);
            setThumbnailItem({
              id: `frame-${Date.now()}`,
              url: URL.createObjectURL(file),
              type: 'image',
              status: 'uploading',
              progress: 0
            });
            try {
              const response = await socialService.uploadImage(file, (progress) => {
                setThumbnailItem(prev => prev ? { ...prev, progress } : null);
              });
              if (response.success && response.data) {
                setThumbnailItem({
                  id: `frame-${Date.now()}`,
                  url: response.data.url,
                  type: 'image',
                  status: 'ready'
                });
                toast.success(t('social.frameSelectedAsThumb', 'Frame definido como thumbnail!'));
              } else {
                throw new Error('Upload failed');
              }
            } catch (err) {
              console.error('Frame upload failed:', err);
              setThumbnailItem(null);
              toast.error(t('social.uploadError', 'Error uploading thumbnail'));
            }
          }}
        />
      )}

      {/* Library picker — choose an existing video or image asset from the user's gallery */}
      <GalleryPicker
        open={showLibrary !== null}
        source={showLibrary}
        onClose={() => setShowLibrary(null)}
        onPick={(asset) => {
          const url = asset.url || asset.thumbnail_url;
          if (!url) {
            toast.error(t('social.assetMissingUrl', 'Esse item não tem URL disponível'));
            return;
          }
          if (showLibrary === 'video') {
            setVideoItem({
              id: `library-${Date.now()}`,
              url,
              type: 'video',
              status: 'ready',
              duration: asset.duration_seconds
            });
          } else {
            setThumbnailItem({
              id: `library-${Date.now()}`,
              url,
              type: 'image',
              status: 'ready'
            });
          }
          setShowLibrary(null);
        }}
      />
    </div>
  );
};


export default AdvancedComposer;
